// web/src/features/caf/CafDetailDev.jsx
import React from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  createAmendmentFromCafFS,
  getCaf,
  listChildAmendmentsFS,
  onCafSnapshot,
  updateCaf,
} from '../../services/firestore/cafService'
import { listCommunitiesFS } from '../../services/firestore/communityService'
import { getActiveLimitsForWorkflowFS } from '../../services/firestore/approvalLimitsService'
import { refreshFacilityTypes } from '../../services/firestore/facilityTypeService'
import { listDocTypesFS } from '../../services/firestore/docTypesService'
import { listMentionableUsersFS, listUsersFS } from '../../services/firestore/usersService'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
// task hooks
import {
  createTaskForCafDraftFS,
  onCafSubmittedTasksFS,
  onCafSentBackTasksFS,
  onCafFinalApprovedTasksFS,
  onCafCompletedTasksFS,
  refreshCafApprovalRoutingFS,
} from '../../services/firestore/taskService'

// Firebase Storage (bucket configured in /src/firebase.js via initializeApp)
import { storage } from '@/firebase'
import { ref as sRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'

/* ---------- constants ---------- */
const COMPLETION_DOC_TYPE = 'Completion Photo'
const COMPLETION_DOC_TYPE_ALIASES = ['Completion Photo', 'Photo - Complete']

/* ---------- helpers ---------- */
function fmtTS(ts) {
  if (!ts) return '—'
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
  if (ts.seconds) return formatter.format(new Date(ts.seconds * 1000))
  const d = new Date(ts)
  return isNaN(d) ? '—' : formatter.format(d)
}
function isValidYmdDate(value) {
  const raw = String(value || '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false
  const d = new Date(`${raw}T00:00:00`)
  return !Number.isNaN(d.getTime())
}
function fmtYmdDate(value) {
  const raw = String(value || '').trim()
  if (!isValidYmdDate(raw)) return '—'
  const [year, month, day] = raw.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString()
}
function uniqueMentionUsers(users = []) {
  const seen = new Set()
  return users.filter((user) => {
    const email = String(user?.email || '').trim().toLowerCase()
    if (!email || seen.has(email) || user?.isActive === false) return false
    seen.add(email)
    return true
  })
}
function filenameFromUrl(url = '') {
  try {
    const u = new URL(url)
    const segs = u.pathname.split('/')
    return decodeURIComponent(segs[segs.length - 1] || '').replace(/\\+/g, ' ')
  } catch {
    return ''
  }
}
function readSession() {
  try {
    return JSON.parse(localStorage.getItem('session') || 'null')
  } catch {
    return null
  }
}
function currentUserLabel() {
  const sess = readSession()
  if (!sess) return 'you@scullycompany.com'
  const nm = sess.displayName || sess.email || 'user'
  const title = sess.title ? `, ${sess.title}` : ''
  return `${nm}${title}`
}

const withUser = (text) => `${text} — ${currentUserLabel()}`

// basic lowercase helper
function lc(s) {
  return (s || '').toString().trim().toLowerCase()
}
function normalizeDocTypeLabel(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}
function isCompletionDocTypeLabel(value) {
  const normalized = normalizeDocTypeLabel(value)
  if (!normalized) return false
  if (COMPLETION_DOC_TYPE_ALIASES.some((label) => normalizeDocTypeLabel(label) === normalized)) {
    return true
  }
  const tokens = new Set(normalized.split(' ').filter(Boolean))
  return tokens.has('photo') && (tokens.has('complete') || tokens.has('completion'))
}
function resolveCompletionDocTypeLabel(docTypes = []) {
  const activeMatch = (docTypes || []).find(
    (row) => row?.isActive !== false && isCompletionDocTypeLabel(row?.name),
  )
  if (activeMatch?.name) return activeMatch.name
  const anyMatch = (docTypes || []).find((row) => isCompletionDocTypeLabel(row?.name))
  return anyMatch?.name || COMPLETION_DOC_TYPE
}

// approver “role” label (matches approvalLimits.role / sequence entries)
function getCurrentUserRoleLabel() {
  const sess = readSession()
  if (!sess) return ''
  if (sess.title) return String(sess.title)
  const roles = Array.isArray(sess.roles) ? sess.roles : []
  return roles[0] || ''
}
function normRole(r) {
  return String(r || '').toLowerCase().replace(/\s+/g, ' ').trim()
}

// sysconfig helper
function isSysconfigAdmin() {
  const sess = readSession()
  if (!sess) return false

  const level = String(sess.adminLevel || sess.admin || '').toLowerCase()
  if (level === 'sysconfig') return true

  const roles = Array.isArray(sess.roles) ? sess.roles : []
  return roles.some((r) => String(r).toLowerCase() === 'sysconfig')
}

function isAdminUser() {
  const sess = readSession()
  if (!sess) return false

  const level = String(sess.adminLevel || sess.admin || '').toLowerCase()
  if (level === 'admin' || level === 'sysconfig') return true

  const roles = Array.isArray(sess.roles) ? sess.roles : []
  return roles.some((r) => {
    const normalized = String(r || '').toLowerCase()
    return normalized === 'admin' || normalized === 'sysconfig'
  })
}

// user-level approver permission
function hasApproverPermission() {
  const sess = readSession()
  if (!sess) return false

  const roles = Array.isArray(sess.roles)
    ? sess.roles.map(lc)
    : []

  const approverRoles = Array.isArray(sess.approverRoles)
    ? sess.approverRoles.map(lc)
    : []

  const level = lc(sess.adminLevel || sess.admin || '')

  // Must explicitly have approver-ish rights
  if (roles.includes('approver')) return true
  if (approverRoles.includes('approver')) return true

  // Allow full admins/sysconfig
  if (level === 'admin' || level === 'sysconfig') return true

  return false
}
// user-level "requester" permission
function hasRequesterPermission() {
  const sess = readSession()
  if (!sess) return false

  const roles = Array.isArray(sess.roles)
    ? sess.roles.map(lc)
    : []

  const level = lc(sess.adminLevel || sess.admin || '')

  // Must explicitly have requester-ish rights
  if (roles.includes('requester')) return true

  // Allow full admins/sysconfig to act as requesters
  if (level === 'admin' || level === 'sysconfig') return true

  return false
}
/* Approval limits → workflow helpers */
function normalizeLimits(rows) {
  const arr = Array.isArray(rows) ? rows : []
  return arr
    .filter((r) => r && r.isActive !== false && r.role)
    .map((r) => ({
      role: String(r.role),
      numericLimit:
        String(r.limit).toLowerCase() === 'unlimited'
          ? Number.POSITIVE_INFINITY
          : Number(String(r.limit || 0).replace(/[, ]+/g, '')) || 0,
      order: Number.isFinite(Number(r.order)) ? Number(r.order) : 9999,
    }))
    .sort((a, b) => {
      if (a.numericLimit !== b.numericLimit) return a.numericLimit - b.numericLimit
      if (a.order !== b.order) return a.order - b.order
      return String(a.role || '').localeCompare(String(b.role || ''))
    })
}

function computeSequenceForAmount(limits, amount) {
  const lims = normalizeLimits(limits)
  const amt = Number(amount || 0)

  if (lims.length === 0) return { sequence: [], finalRole: null }

  let finalIdx = -1
  for (let i = 0; i < lims.length; i++) {
    if (lims[i].numericLimit >= amt) {
      finalIdx = i
      break
    }
  }
  if (finalIdx === -1) finalIdx = lims.length - 1

  const sequence = lims.slice(0, finalIdx + 1).map((r) => r.role)
  const finalRole = lims[finalIdx]?.role || null
  return { sequence, finalRole }
}

function parseCurrencyAmount(value) {
  if (value == null || value === '') return null
  const cleaned = String(value).trim().replace(/[$,\s]/g, '')
  if (!cleaned) return null
  const amount = Number(cleaned)
  return Number.isFinite(amount) ? amount : null
}

function getValidSubmissionAmount(value) {
  if (value == null || value === '') return null
  const amount = parseCurrencyAmount(value)
  if (!Number.isFinite(amount) || amount <= 0) return null
  return amount
}
function canSubmitWithBudget(value, notBudgeted) {
  if (notBudgeted === true) return true
  return getValidSubmissionAmount(value) != null
}
function getRecommendedProposalAmount(caf) {
  const proposals = Array.isArray(caf?.proposals) ? caf.proposals : []
  const recommended = proposals.find((p) => p?.recommended === true)
  return getValidSubmissionAmount(recommended?.price)
}
function getApprovalBasisAmount(caf) {
  const recommendedAmount = getRecommendedProposalAmount(caf)
  if (caf?.notBudgeted === true) {
    return recommendedAmount
  }
  if (caf?.isAmendment === true) {
    return getValidSubmissionAmount(
      caf?.revisedTotalAmount ?? caf?.contractPrice,
    )
  }
  const budgetedAmount = getValidSubmissionAmount(caf?.contractPrice)
  if (budgetedAmount == null) return recommendedAmount
  if (recommendedAmount == null) return budgetedAmount
  return Math.max(budgetedAmount, recommendedAmount)
}
function getApprovalBasisLabel(caf) {
  if (caf?.notBudgeted === true) return 'recommended proposal amount'
  if (caf?.isAmendment === true) return 'revised project total'
  const budgetedAmount = getValidSubmissionAmount(caf?.contractPrice)
  const recommendedAmount = getRecommendedProposalAmount(caf)
  return recommendedAmount != null &&
    (budgetedAmount == null || recommendedAmount > budgetedAmount)
    ? 'recommended proposal amount'
    : 'budgeted amount'
}
function formatMoney(value) {
  if (value == null || value === '') return '—'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '—'
  return `$${amount.toLocaleString()}`
}

// (kept for reference but not used for “final” anymore)
function nextStageFromSequence(sequence, currentStage) {
  const seq = Array.isArray(sequence) ? sequence : []
  if (seq.length === 0) return { next: 'approved', isFinal: true }

  const cur = currentStage || seq[0]
  const idx = seq.indexOf(cur)
  if (idx === -1) return { next: seq[0], isFinal: seq.length === 1 }
  if (idx >= seq.length - 1) return { next: 'approved', isFinal: true }
  return { next: seq[idx + 1], isFinal: idx + 1 === seq.length - 1 }
}

/* legacy file keys we might encounter */
const FILE_KEYS = ['files', 'documents', 'docs', 'attachments']
function detectFilesKey(caf) {
  for (const k of FILE_KEYS) {
    const v = caf?.[k]
    if (Array.isArray(v) && v.length > 0) return k
  }
  return 'files'
}
function normalizeCafFiles(doc) {
  if (!doc || typeof doc !== 'object') return doc
  const fromFirstPopulated =
    (Array.isArray(doc.files) && doc.files.length)
      ? doc.files
      : (Array.isArray(doc.documents) && doc.documents.length)
      ? doc.documents
      : (Array.isArray(doc.docs) && doc.docs.length)
      ? doc.docs
      : (Array.isArray(doc.attachments) && doc.attachments.length)
      ? doc.attachments
      : []
  return { ...doc, files: fromFirstPopulated }
}

/* Storage helpers */
function getCafStorageDirKey(caf) {
  if (!caf || typeof caf !== 'object') return 'unknownCAF'
  return String(caf.id || caf.docId || caf.cafId || caf.number || 'unknownCAF')
}
function getCafLabelForFilename(caf) {
  if (!caf || typeof caf !== 'object') return 'CAF'
  return String(caf.number || caf.cafNumber || caf.code || getCafStorageDirKey(caf))
}
function getSafeStorageSegment(value, fallback = 'unknown') {
  return String(value || fallback)
    .trim()
    .replace(/[\/?#\[\]*]/g, '_')
}

async function uploadToStorageForCaf(caf, folder, file) {
  const dirKey = getCafStorageDirKey(caf)
  const safeDir = getSafeStorageSegment(dirKey, 'unknownCAF')

  const originalName = file?.name || 'upload'
  const safeName = `${crypto.randomUUID()}_${originalName.replace(/[^a-zA-Z0-9._-]/g, '_')}`

  const fullPath = `caf/${safeDir}/${folder}/${safeName}`

  const ref = sRef(storage, fullPath)
  await uploadBytes(ref, file, { contentType: file.type || 'application/octet-stream' })
  const downloadURL = await getDownloadURL(ref)
  return {
    name: safeName,
    size: file.size,
    type: file.type || 'application/octet-stream',
    downloadURL,
    path: fullPath,
    ts: new Date().toISOString(),
  }
}

const CAN_DELETE_BLOBS_STATUSES = new Set(['draft', 'awaiting_approval', 'approved'])

/* ---------- tolerant MIME helpers ---------- */
function isPdfFile(file) {
  if (!file) return false
  const type = String(file.type || '').toLowerCase()
  if (type.includes('pdf')) return true
  const name = String(file.name || '').toLowerCase()
  return name.endsWith('.pdf')
}
function isAllowedDoc(file) {
  if (!file) return false
  const type = String(file.type || '').toLowerCase()
  if (type.includes('pdf') || type.startsWith('image/')) return true
  const name = String(file.name || '').toLowerCase()
  return ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp'].some((ext) =>
    name.endsWith(ext),
  )
}
function isImageFile(file) {
  if (!file) return false
  const type = String(file.type || '').toLowerCase()
  if (type && type !== 'application/octet-stream') return ['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(type)
  const name = String(file.name || '').toLowerCase()
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some((ext) =>
    name.endsWith(ext),
  )
}

// For completion photo requirement we want an *image* file
function isImageLikeFileMeta(fileMeta) {
  if (!fileMeta) return false
  const mime = String(fileMeta.type || fileMeta.file?.type || '').toLowerCase()
  if (mime && mime !== 'application/octet-stream') return ['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(mime)

  const name = String(
    fileMeta.name ||
      fileMeta.file?.name ||
      filenameFromUrl(fileMeta.file?.downloadURL) ||
      '',
  ).toLowerCase()

  return ['.png', '.jpg', '.jpeg', '.gif', '.webp'].some((ext) => name.endsWith(ext))
}

function hasCompletionPhoto(caf) {
  if (!caf || !Array.isArray(caf.files)) return false
  return caf.files.some((f) => {
    const docType = String(f.docType || f.type || f.category || '').trim().toLowerCase()
    return isCompletionDocTypeLabel(docType) && isImageLikeFileMeta(f.file || f)
  })
}

/* ---------- pill coloring for approver chain ---------- */
function buildStageStatusMap(sequence, caf) {
  const seq = Array.isArray(sequence) ? sequence : []
  const cur = caf?.stage || seq[0] || null
  const curIdx = seq.indexOf(cur)
  const status = String(caf?.status || '').toLowerCase()
  const hist = Array.isArray(caf?.approvals?.history) ? caf.approvals.history : []
  const last = hist.length ? hist[hist.length - 1] : null

  const map = new Map()
  for (let i = 0; i < seq.length; i++) {
    const role = seq[i]
    map.set(role, 'pending')
  }

  for (const h of hist) {
    if (h?.action === 'approved' && h?.role && map.has(h.role)) {
      map.set(h.role, 'done')
    }
  }
  if (curIdx > 0) {
    for (let i = 0; i < curIdx; i++) {
      const r = seq[i]
      map.set(r, 'done')
    }
  }

  if (status === 'changes_requested') {
    if (last?.action === 'sent_back' && last?.role && map.has(last.role)) {
      map.set(last.role, 'sent_back')
    } else if (cur && map.has(cur)) {
      map.set(cur, 'sent_back')
    }
  } else if (status === 'approved' || status === 'complete') {
    // final approved/complete: leave all done; no "current" pill
  } else if (cur && map.has(cur)) {
    map.set(cur, 'current')
  }

  return map
}

function styleForStageState(state) {
  switch (state) {
    case 'done':
      return { background: '#e6f4ea', color: '#1e7e34', border: '1px solid #c7ebd1' }
    case 'sent_back':
      return { background: '#fdecea', color: '#b3261e', border: '1px solid #f5c2c0' }
    case 'current':
      return { background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe' }
    default:
      return {}
  }
}

/* ---------- muted pencil icon ---------- */
function PencilIcon({ size = 16, color = '#6b7280' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  )
}

function parseTagsInput(raw) {
  const parts = String(raw || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
  const seen = new Set()
  const out = []
  for (const t of parts) {
    const key = t.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(t)
  }
  return out
}

function tagsToDraft(tags) {
  return Array.isArray(tags) ? tags.join(', ') : ''
}

export default function CafDetailDev() {
  const { id } = useParams()
  const nav = useNavigate()

  const [caf, setCaf] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [err, setErr] = React.useState('')

  // header comment
  const [comment, setComment] = React.useState('')

  // dropdown data
  const [facilityTypes, setFacilityTypes] = React.useState([])
  const [docTypes, setDocTypes] = React.useState([])
  const [commMap, setCommMap] = React.useState(new Map())
  const [facilityEditOpen, setFacilityEditOpen] = React.useState(false)
  const [tagsEditOpen, setTagsEditOpen] = React.useState(false)
  const [tagsDraft, setTagsDraft] = React.useState('')
  const [savingTags, setSavingTags] = React.useState(false)

  // messages
  const [msgText, setMsgText] = React.useState('')
  const [mentionableUsers, setMentionableUsers] = React.useState([])
  const [selectedMentions, setSelectedMentions] = React.useState([])
  const [mentionMenuPos, setMentionMenuPos] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  })
  const [mentionMenu, setMentionMenu] = React.useState({
    open: false,
    query: '',
    start: -1,
    end: -1,
    activeIndex: 0,
  })
  const msgTextareaRef = React.useRef(null)

  // project completion
  const [budget, setBudget] = React.useState('')
  const [actual, setActual] = React.useState('')
  const actualDirtyRef = React.useRef(false)
  const actualDirtyCafIdRef = React.useRef('')

  // proposals inputs
  const [pvVendor, setPvVendor] = React.useState('')
  const [pvPrice, setPvPrice] = React.useState('')
  const [pvNotes, setPvNotes] = React.useState('')
  const [pvFile, setPvFile] = React.useState(null)
  const [pvFileKey, setPvFileKey] = React.useState(0)
  const [proposalNoteTooltip, setProposalNoteTooltip] = React.useState(null)
  const [editingProposalPriceIdx, setEditingProposalPriceIdx] = React.useState(null)
  const [editingProposalPriceDraft, setEditingProposalPriceDraft] = React.useState('')
  const [savingProposalPrice, setSavingProposalPrice] = React.useState(false)
  const [confirmRemove, setConfirmRemove] = React.useState({
    open: false,
    kind: '',
    idx: null,
  })
  const [submitValidationDialog, setSubmitValidationDialog] = React.useState({
    open: false,
    title: '',
    message: '',
  })
  const [voidDialog, setVoidDialog] = React.useState({
    open: false,
    reason: '',
    error: '',
    saving: false,
  })
  const [amendmentDialog, setAmendmentDialog] = React.useState({
    open: false,
    reason: '',
    error: '',
    saving: false,
  })
  const [refreshRoutingBusy, setRefreshRoutingBusy] = React.useState(false)
  const [childAmendments, setChildAmendments] = React.useState([])
  const [parentCafSummary, setParentCafSummary] = React.useState(null)

  // files inputs
  const [fileTitle, setFileTitle] = React.useState('')
  const [fileNotes, setFileNotes] = React.useState('')
  const [fileTags, setFileTags] = React.useState('')
  const [fileDocType, setFileDocType] = React.useState('')
  const [chosenFile, setChosenFile] = React.useState(null)
  const [fileInputKey, setFileInputKey] = React.useState(0)

  const [uploadingProposal, setUploadingProposal] = React.useState(false)
  const [uploadingFile, setUploadingFile] = React.useState(false)

  // collapsibles
  const [msgsOpen, setMsgsOpen] = React.useState(false)
  const [filesOpen, setFilesOpen] = React.useState(false)
  const [timelineOpen, setTimelineOpen] = React.useState(false)

  const filesKey = React.useMemo(() => detectFilesKey(caf || {}), [caf]) // eslint-disable-line no-unused-vars

  // ref for Files & Images file input (for completion photos)
  const fileInputRef = React.useRef(null)

  // approval limits (from Firestore)
  const [limits, setLimits] = React.useState([])
  const approvalAmountValue = getApprovalBasisAmount(caf) || 0
  const sequenceForAmount = React.useMemo(() => {
    return computeSequenceForAmount(limits, approvalAmountValue)
  }, [limits, approvalAmountValue])

  // budgeted amount edit state
  const [contractEditOpen, setContractEditOpen] = React.useState(false)
  const [contractDraft, setContractDraft] = React.useState('')
  const [contractNote, setContractNote] = React.useState('')
  const [contractNotBudgetedDraft, setContractNotBudgetedDraft] = React.useState(false)
  const [savingContract, setSavingContract] = React.useState(false)
  const [completionDateEditOpen, setCompletionDateEditOpen] = React.useState(false)
  const [completionDateDraft, setCompletionDateDraft] = React.useState('')
  const [completionDateNote, setCompletionDateNote] = React.useState('')
  const [savingCompletionDate, setSavingCompletionDate] = React.useState(false)

  // sysconfig override checkbox
  const [overrideApprovalChain, setOverrideApprovalChain] = React.useState(false)
  const sysconfigAdmin = isSysconfigAdmin()
  const adminUser = isAdminUser()

  // // present warn for non approver with manager title
  // const lc = (v) => String(v || '').toLowerCase().trim()

  // derived: completion photo present
  const completionPhotoOnFile = React.useMemo(
    () => hasCompletionPhoto(caf),
    [caf],
  )
  const completionDocTypeLabel = React.useMemo(
    () => resolveCompletionDocTypeLabel(docTypes),
    [docTypes],
  )

  // -------- Load CAF + lookups --------
  React.useEffect(() => {
    let alive = true
    let unsubscribeCaf = null
    ;(async () => {
      try {
        setLoading(true)
        const [comms, lim, types, docs] = await Promise.all([
          listCommunitiesFS(),
          getActiveLimitsForWorkflowFS(),
          refreshFacilityTypes(),
          listDocTypesFS(),
        ])
        if (!alive) return

        const map = new Map(
          (comms || []).map((c) => [
            c.code || c.name || c.id,
            c.communityName || c.name || c.code || c.id,
          ]),
        )
        setCommMap(map)

        const activeTypeNames = (types || [])
          .filter((t) => t.isActive !== false)
          .map((t) => t.name)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b))
        setFacilityTypes(activeTypeNames)

        const activeDocTypes = (docs || [])
          .filter((d) => d.isActive !== false)
          .map((d) => ({
            id: d.id || d.code || d.name,
            name: d.name || d.code || '',
          }))
          .filter((d) => d.name)
          .sort((a, b) => a.name.localeCompare(b.name))
        setDocTypes(activeDocTypes)

        setLimits(lim || [])
        ;(async () => {
          try {
            const users = await listMentionableUsersFS()
            if (!alive) return
            const normalized = uniqueMentionUsers(users || [])
            if (normalized.length) {
              setMentionableUsers(normalized)
              return
            }
          } catch (error) {
            console.warn('[CafDetail] failed to load mentionable users', error)
          }

          // Fallback: for admins/sysconfig only, try direct Firestore list (some tenants block list
          // operations for non-admin users).
          if (!alive) return
          if (!isAdminUser()) {
            setMentionableUsers([])
            return
          }
          try {
            const rows = await listUsersFS()
            if (!alive) return
            const mapped = (rows || [])
              .filter((u) => u && u.isActive !== false)
              .map((u) => ({
                email: u.email || u.id || '',
                name: u.displayName || u.name || u.email || u.id || '',
              }))
              .filter((u) => u.email && u.name)
            setMentionableUsers(uniqueMentionUsers(mapped))
          } catch (error) {
            console.warn('[CafDetail] failed to load mentionable users fallback', error)
            if (!alive) return
            setMentionableUsers([])
          }
        })()
        unsubscribeCaf = onCafSnapshot(id, (data) => {
          if (!alive) return
          const normalized = normalizeCafFiles(data)
          setCaf(normalized)
          setContractDraft(
            normalized?.contractPrice != null && normalized.contractPrice !== ''
              ? String(normalized.contractPrice)
              : '',
          )
          setContractNotBudgetedDraft(normalized?.notBudgeted === true)
          setLoading(false)
        })
      } catch (e) {
        console.error(e)
        setErr('Failed to load CAF.')
      } finally {
        if (alive && !unsubscribeCaf) setLoading(false)
      }
    })()
    return () => {
      alive = false
      if (unsubscribeCaf) unsubscribeCaf()
    }
  }, [id])

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      if (!caf?.id) {
        if (alive) {
          setChildAmendments([])
          setParentCafSummary(null)
        }
        return
      }
      try {
        const amendmentChainRootId =
          caf.rootCafId || caf.parentCafId || caf.id
        const [children, parent] = await Promise.all([
          listChildAmendmentsFS(caf.isAmendment === true ? amendmentChainRootId : caf.id),
          caf.parentCafId ? getCaf(caf.parentCafId) : Promise.resolve(null),
        ])
        if (!alive) return
        setChildAmendments(children)
        setParentCafSummary(parent)
      } catch (e) {
        console.warn('[CafDetail] failed to load amendment relationships', e)
        if (!alive) return
        setChildAmendments([])
        setParentCafSummary(null)
      }
    })()
    return () => {
      alive = false
    }
  }, [caf?.id, caf?.parentCafId, caf?.rootCafId, caf?.isAmendment])

  const mentionSuggestions = React.useMemo(() => {
    if (!mentionMenu.open) return []
    const query = String(mentionMenu.query || '').trim().toLowerCase()
    const rows = mentionableUsers
      .map((user) => {
        const name = String(user.displayName || user.name || user.email || '').trim()
        const email = String(user.email || '').trim().toLowerCase()
        return {
          name: name || email,
          email,
          search: `${name} ${email}`.toLowerCase(),
        }
      })
      .filter((user) => user.email)

    if (!query) return rows.slice(0, 6)
    return rows.filter((user) => user.search.includes(query)).slice(0, 6)
  }, [mentionMenu, mentionableUsers])

  function resetMentionMenu() {
    setMentionMenu({
      open: false,
      query: '',
      start: -1,
      end: -1,
      activeIndex: 0,
    })
  }

  function updateMentionMenuPos() {
    const el = msgTextareaRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const padX = 12
    const availableWidth = Math.max(0, rect.width - padX * 2)
    setMentionMenuPos({
      top: rect.bottom + 6,
      left: rect.left + padX,
      width: Math.min(280, availableWidth),
    })
  }

  function syncMentionMenu(nextText, caretIndex) {
    const beforeCaret = nextText.slice(0, caretIndex)
    const match = beforeCaret.match(/(^|\s)@([a-z0-9._ -]{0,50})$/i)
    if (!match) {
      resetMentionMenu()
      return
    }
    const query = match[2] || ''
    const triggerIndex = caretIndex - query.length - 1
    updateMentionMenuPos()
    setMentionMenu({
      open: true,
      query,
      start: triggerIndex,
      end: caretIndex,
      activeIndex: 0,
    })
  }

  function applyMention(user) {
    if (!user || mentionMenu.start < 0 || mentionMenu.end < 0) return
    const displayName = String(user.name || user.displayName || user.email || '').trim()
    const email = String(user.email || '').trim().toLowerCase()
    const insertText = `@${displayName} `
    const nextText =
      `${msgText.slice(0, mentionMenu.start)}${insertText}${msgText.slice(mentionMenu.end)}`
    setMsgText(nextText)
    setSelectedMentions((prev) => {
      const seen = new Set()
      return uniqueMentionUsers(
        [...prev, { email, displayName }]
          .filter((row) => {
            const key = String(row?.email || '').trim().toLowerCase()
            if (!key || seen.has(key)) return false
            seen.add(key)
            return true
          })
      )
    })
    resetMentionMenu()
    requestAnimationFrame(() => {
      if (!msgTextareaRef.current) return
      const caret = mentionMenu.start + insertText.length
      msgTextareaRef.current.focus()
      msgTextareaRef.current.setSelectionRange(caret, caret)
    })
  }

  React.useEffect(() => {
    if (!mentionMenu.open) return
    updateMentionMenuPos()
    const onScroll = () => updateMentionMenuPos()
    const onResize = () => updateMentionMenuPos()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [mentionMenu.open])

  // Seed / refresh project completion fields whenever CAF changes
  React.useEffect(() => {
    if (!caf) return
    if (actualDirtyCafIdRef.current !== caf.id) {
      actualDirtyCafIdRef.current = caf.id
      actualDirtyRef.current = false
    }

    const budgetVal =
      caf.projectBudget ??
      caf.projectBudgetAmount ??
      caf.projectBudgetCost ??
      null

    const actualVal =
      caf.projectActual ??
      caf.projectActualAmount ??
      caf.actualProjectAmount ??
      null

    setBudget(
      budgetVal != null
        ? String(budgetVal)
        : caf.contractPrice != null
        ? String(caf.contractPrice)
        : '',
    )

    if (!actualDirtyRef.current) {
      setActual(actualVal != null ? String(actualVal) : '')
    }
    if (!completionDateEditOpen) {
      setCompletionDateDraft(String(caf.projectCompletionDate || '').trim())
      setCompletionDateNote('')
    }
    if (!tagsEditOpen) setTagsDraft(tagsToDraft(caf.tags))
  }, [caf, completionDateEditOpen, tagsEditOpen])

  function getCommunityName(code) {
    if (!code) return '—'
    return commMap.get(code) || code
  }
  const addTimeline = (event) => [
    ...(caf?.timeline || []),
    { ts: new Date().toISOString(), event },
  ]
  function currentStatusLower() {
    return lc(caf?.status || '')
  }
  function getFinalApprovalLockMessage() {
    if (currentStatusLower() === 'void') {
      return 'This CAF has been voided and can no longer be edited.'
    }
    if (hasNewerAmendment) {
      return `A newer amendment exists. Project completion must be managed through ${projectCompletionOwnerText || 'the latest amendment record'}.`
    }
    if (caf?.isAmendment !== true && caf?.activeAmendmentId && lc(caf?.activeAmendmentStatus || '') !== 'void' && lc(caf?.activeAmendmentStatus || '') !== 'complete') {
      return 'This CAF has an active amendment. Project completion must be managed through the amendment record until that amendment is resolved.'
    }
    if (hasAmendmentChain) {
      return `This CAF remains the original approved record. Project completion must be managed through ${projectCompletionOwnerText || 'the latest amendment record'}.`
    }
    return currentStatusLower() === 'complete'
      ? 'This CAF is complete and can no longer be edited.'
      : 'This CAF is locked after final approval. Only the project completion date, actual amount, and files/documents can be updated until the CAF is marked complete.'
  }
  function ensureApprovalSensitiveEditAllowed() {
    const status = currentStatusLower()
    if (status !== 'approved' && status !== 'complete' && status !== 'void') return true
    setErr(getFinalApprovalLockMessage())
    return false
  }
  function ensureProjectCompletionEditAllowed() {
    if (caf?.isAmendment !== true && caf?.activeAmendmentId && lc(caf?.activeAmendmentStatus || '') !== 'void' && lc(caf?.activeAmendmentStatus || '') !== 'complete') {
      setErr(getFinalApprovalLockMessage())
      return false
    }
    if (currentStatusLower() !== 'complete' && currentStatusLower() !== 'void') return true
    setErr(getFinalApprovalLockMessage())
    return false
  }
  function ensureFileEditAllowed(docType = '') {
    const status = currentStatusLower()
    if (status === 'complete' || status === 'void') {
      setErr(getFinalApprovalLockMessage())
      return false
    }
    return true
  }
  function openSubmitValidationDialog(title, message) {
    setSubmitValidationDialog({
      open: true,
      title,
      message,
    })
  }
  function openVoidDialog() {
    setVoidDialog({
      open: true,
      reason: '',
      error: '',
      saving: false,
    })
  }
  function openAmendmentDialog() {
    setAmendmentDialog({
      open: true,
      reason: '',
      error: '',
      saving: false,
    })
  }

  // jump to Files & Images and start completion photo upload
  function goToCompletionPhotos() {
    if (!ensureProjectCompletionEditAllowed()) return
    setFileDocType(completionDocTypeLabel)
    setFileTitle((prev) => prev || completionDocTypeLabel)
    // open Files & Images panel
    setFilesOpen(true)

    // after DOM updates, scroll + trigger the file chooser
    setTimeout(() => {
      const el = document.getElementById('caf-files-card')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      if (fileInputRef.current) {
        fileInputRef.current.click()
      }
    }, 150)
  }

  /* --------- approval actions (dynamic sequence) --------- */

  // submit: CAF update separated from task hook
  // submit: CAF update separated from task hook
  async function doSubmit() {
    setErr('')

    const amount = getValidSubmissionAmount(caf?.contractPrice)
    const validBudgetForSubmit = canSubmitWithBudget(caf?.contractPrice, caf?.notBudgeted)
    const approvalAmount = getApprovalBasisAmount(caf)
    const completionDate = String(caf?.projectCompletionDate || '').trim()
    const approvalBasisLabel = getApprovalBasisLabel(caf)

    if (!validBudgetForSubmit && !isValidYmdDate(completionDate)) {
      openSubmitValidationDialog(
        'Missing Submission Details',
        'Please enter a budgeted amount greater than $0 (or mark the CAF as Not Budgeted) and a project completion date before submitting.',
      )
      return
    }
    if (!validBudgetForSubmit) {
      openSubmitValidationDialog(
        'Budget Required',
        'Please enter a budgeted amount greater than $0 before submitting, or mark the CAF as Not Budgeted.',
      )
      return
    }
    if (!isValidYmdDate(completionDate)) {
      openSubmitValidationDialog(
        'Completion Date Required',
        'Please enter a project completion date before submitting.',
      )
      return
    }
    if (!approvalAmount) {
      openSubmitValidationDialog(
        'Approval Amount Required',
        caf?.notBudgeted === true
          ? 'Select a recommended proposal with an amount greater than $0 before submitting a Not Budgeted CAF. The recommended proposal amount determines the approval chain.'
          : `Please enter a valid ${approvalBasisLabel} before submitting.`,
      )
      return
    }
    const { sequence } = computeSequenceForAmount(limits, approvalAmount)
    const firstStage = sequence && sequence.length ? sequence[0] : caf.stage || 'Manager'

    let fresh = null

    try {
      await updateCaf(caf.id, {
        status: 'awaiting_approval',
        stage: firstStage,
        // 🔹 remember where the approval chain started so we can send it back there
        startedStage: firstStage,
        approvalBasisAmount: approvalAmount,
        approvalBasisLabel,
        approvals: {
          sequence,
          history: [
            ...(caf.approvals?.history || []),
            {
              role: '(system)',
              action: 'submitted',
              by: currentUserLabel(),
              comment: comment || 'Submitted',
              ts: new Date().toISOString(),
            },
          ],
        },
        timeline: addTimeline(
          withUser(`Submitted${comment ? ` — ${comment}` : ''}`),
        ),
      })

      fresh = normalizeCafFiles(await getCaf(caf.id))
      setCaf(fresh)
      setComment('')
    } catch (e) {
      console.error('[CAF] submit failed', e)
      setErr('Failed to submit.')
      return
    }

    if (!fresh) return
    try {
      await onCafSubmittedTasksFS(fresh)
    } catch (hookErr) {
      console.error('[CAF] task hook after submit failed', hookErr)
    }
  }

  async function doCreateAmendment() {
    if (amendmentDialog.saving) return
    const reason = String(amendmentDialog.reason || '').trim()
    if (!reason) {
      setAmendmentDialog((prev) => ({
        ...prev,
        error: 'Please provide a reason for the amendment.',
      }))
      return
    }

    try {
      setAmendmentDialog((prev) => ({ ...prev, saving: true, error: '' }))
      const sess = readSession() || {}
      const amendmentId = await createAmendmentFromCafFS(caf, {
        reason,
        createdByEmail: sess.email || '',
        createdByName: sess.displayName || sess.email || '',
      })
      const amendment = await getCaf(amendmentId)
      if (amendment) {
        await createTaskForCafDraftFS(amendment)
      }
      setAmendmentDialog({
        open: false,
        reason: '',
        error: '',
        saving: false,
      })
      nav(`/dev/caf/${encodeURIComponent(amendmentId)}`)
    } catch (e) {
      console.error('[CAF] create amendment failed', e)
      setAmendmentDialog((prev) => ({
        ...prev,
        saving: false,
        error: 'Failed to create amendment.',
      }))
    }
  }


    // APPROVE: only mark approved when *final* role in sequence approves
  async function doApprove() {
    setErr('')

    if (!approvalAmountValue) {
      setErr(
        caf?.notBudgeted === true
          ? 'A Not Budgeted CAF needs a recommended proposal amount before it can be approved.'
          : 'This CAF needs a valid approval amount before it can be approved.',
      )
      return
    }

    // Always prefer the current amount-derived sequence so later budget edits
    // cannot leave us approving against a stale chain.
    const recomputedSequence =
      computeSequenceForAmount(limits, approvalAmountValue).sequence
    const sequence =
      recomputedSequence && recomputedSequence.length
        ? recomputedSequence
        : Array.isArray(caf?.approvals?.sequence) && caf.approvals.sequence.length
          ? caf.approvals.sequence
          : []

    if (!sequence || sequence.length === 0) {
      setErr('No approval limits are configured for this amount.')
      return
    }

    const cur = caf.stage || sequence[0] || 'Manager'
    const curIdx = sequence.indexOf(cur)
    const finalIdx = sequence.length - 1

    const userRole = getCurrentUserRoleLabel()
    const userIdx = sequence.findIndex(
      (r) => normRole(r) === normRole(userRole),
    )

    // your existing flag for override
    const canOverride = sysconfigAdmin && overrideApprovalChain

    // NEW: Global gate – must have approver permission unless overriding as sysconfig
    const userCanApprove = hasApproverPermission()

    if (!userCanApprove && !canOverride) {
      setErr('You do not have approval permissions for this CAF. Please contact an admin.')
      return
    }

    if (curIdx === -1) {
      setErr(`This CAF is in an unknown stage (“${cur}”). Please contact an admin.`)
      return
    }

    // If not in the correct stage AND no override, block
    if (userIdx !== curIdx && !canOverride) {
      const expected = sequence[curIdx]
      setErr(
        `You can’t approve at this step. Current approver role: ${expected}. ` +
          `Your role: ${userRole || 'unknown'}.`,
      )
      return
    }

    // 🔹 Where should the CAF "live" after final approval?
    // Prefer the recorded startedStage; fall back to first in sequence or "Manager"
    const startStage = caf.startedStage || sequence[0] || 'Manager'

    const isFinal = curIdx === finalIdx
    const newStage = isFinal ? startStage : sequence[curIdx + 1]
    const newStatus = isFinal ? 'approved' : 'in_review'

    // Build history entry WITHOUT undefined fields
    const historyEntry = {
      role: cur,
      action: 'approved',
      by: currentUserLabel(),
      comment:
        (comment || 'Approved') +
        (canOverride ? ' (sysconfig override)' : ''),
      ts: new Date().toISOString(),
    }

    if (canOverride) {
      historyEntry.override = true
    }

    const timelineLabel =
      `Approved at ${cur}` +
      (isFinal ? ' (final approval – returned to submitter for execution)' : '') +
      (canOverride ? ' (sysconfig override of approver chain)' : '') +
      (comment ? ` — ${comment}` : '')

    // Build changes object, conditionally adding override metadata
    const changes = {
      stage: newStage,
      status: newStatus,
      approvals: {
        sequence,
        history: [...(caf.approvals?.history || []), historyEntry],
      },
      timeline: addTimeline(withUser(timelineLabel)),
    }

    if (canOverride) {
      changes.approvalsOverride = {
        usedBy: currentUserLabel(),
        usedAt: new Date().toISOString(),
        fromStage: cur,
        toStage: newStage,
      }
    }

    let fresh = null

    try {
      await updateCaf(caf.id, changes)

      fresh = normalizeCafFiles(await getCaf(caf.id))
      setCaf(fresh)
      setComment('')
      if (canOverride) {
        setOverrideApprovalChain(false) // reset the checkbox after override
      }
    } catch (e) {
      console.error('[CAF] core approve failed', e)
      setErr('Failed to approve/advance.')
      return
    }

    if (!fresh) return
    try {
      if (isFinal) {
        // 🔹 Final approval hook – this is where your "execute project" task is created
        await onCafFinalApprovedTasksFS(fresh)
      } else {
        await onCafSubmittedTasksFS(fresh)
      }
    } catch (hookErr) {
      console.error('[CAF] task hook after approve failed', hookErr)
    }
  }

  async function doSendBack() {
    const statusLower = String(caf.status || '').toLowerCase()
    if (statusLower !== 'awaiting_approval' && statusLower !== 'in_review') {
      setErr('CAF can only be sent back while it is in the approval flow.')
      return
    }

    const userCanApprove = hasApproverPermission()
    if (!userCanApprove && !sysconfigAdmin) {
      setErr('You do not have permission to send this CAF back.')
      return
    }

    try {
      setErr('')
      const start =
        (caf?.approvals?.sequence && caf.approvals.sequence[0]) || 'Manager'
      await updateCaf(caf.id, {
        status: 'changes_requested',
        stage: start,
        startedStage: start,
        approvals: {
          sequence:
            caf?.approvals?.sequence ||
            computeSequenceForAmount(limits, approvalAmountValue).sequence,
          history: [
            ...(caf.approvals?.history || []),
            {
              role: caf.stage || start,
              action: 'sent_back',
              by: currentUserLabel(),
              comment: comment || 'Please revise and resubmit',
              ts: new Date().toISOString(),
            },
          ],
        },
        timeline: addTimeline(
          withUser(
            `Sent back to ${start}${comment ? ` — ${comment}` : ''} — Submit again after changes to restart the approval chain`,
          ),
        ),
      })

      const fresh = normalizeCafFiles(await getCaf(caf.id))
      setCaf(fresh)
      setComment('')

      await onCafSentBackTasksFS(fresh)
    } catch (e) {
      console.error(e)
      setErr('Failed to send back.')
    }
  }

  async function doVoidCaf() {
    if (voidDialog.saving) return
    if (!sysconfigAdmin) {
      setErr('Only sysconfig admins can void a CAF.')
      return
    }

    const reason = String(voidDialog.reason || '').trim()
    if (!reason) {
      setVoidDialog((prev) => ({
        ...prev,
        error: 'Please provide a reason for voiding this CAF.',
      }))
      return
    }

    try {
      setErr('')
      setVoidDialog((prev) => ({ ...prev, saving: true, error: '' }))
      await updateCaf(caf.id, {
        status: 'void',
        stage: 'Voided',
        voidReason: reason,
        voidedAt: new Date().toISOString(),
        voidedBy: currentUserLabel(),
        timeline: addTimeline(withUser(`CAF voided — ${reason}`)),
      })

      const fresh = normalizeCafFiles(await getCaf(caf.id))
      setCaf(fresh)
      setComment('')
      setVoidDialog({
        open: false,
        reason: '',
        error: '',
        saving: false,
      })
    } catch (e) {
      console.error(e)
      setVoidDialog((prev) => ({
        ...prev,
        saving: false,
        error: 'Failed to void CAF.',
      }))
    }
  }

  async function doRefreshApprovalRouting() {
    if (!adminUser) {
      setErr('Only admins can refresh approval routing.')
      return
    }

    const statusLower = String(caf?.status || '').toLowerCase()
    if (statusLower !== 'awaiting_approval' && statusLower !== 'in_review') {
      setErr('Approval routing can only be refreshed while the CAF is in the approval flow.')
      return
    }

    try {
      setErr('')
      setRefreshRoutingBusy(true)
      await updateCaf(caf.id, {
        timeline: addTimeline(withUser('Approval routing refreshed by admin')),
      })
      const fresh = normalizeCafFiles(await getCaf(caf.id))
      setCaf(fresh)
      await refreshCafApprovalRoutingFS(fresh)
      const latest = normalizeCafFiles(await getCaf(caf.id))
      setCaf(latest)
    } catch (e) {
      console.error(e)
      setErr('Failed to refresh approval routing.')
    } finally {
      setRefreshRoutingBusy(false)
    }
  }

  /* --------- messages --------- */
  async function postMessage() {
    if (!ensureApprovalSensitiveEditAllowed()) return
    const body = msgText.trim()
    if (!body) return
    const sess = readSession() || {}
    const mentionedEmails = selectedMentions
      .map((user) => ({
        email: String(user?.email || '').trim().toLowerCase(),
        token: `@${String(user?.displayName || user?.name || user?.email || '').trim()}`,
      }))
      .filter((row) => row.email && row.token.length > 1 && body.includes(row.token))
      .map((row) => row.email)
    const entry = {
      by: currentUserLabel(),
      byName: sess.displayName || sess.name || sess.email || '',
      byEmail: String(sess.email || '').trim().toLowerCase(),
      mentionedEmails,
      body,
      ts: new Date().toISOString(),
    }
    try {
      await updateCaf(caf.id, {
        messages: [...(caf.messages || []), entry],
        timeline: addTimeline(withUser('Message posted')),
      })
      setCaf(normalizeCafFiles(await getCaf(caf.id)))
      setMsgText('')
      setSelectedMentions([])
      resetMentionMenu()
      setMsgsOpen(true)
    } catch (e) {
      console.error(e)
      setErr('Failed to post message.')
    }
  }

  /* --------- proposals --------- */
  const proposals = caf?.proposals || []
  const proposalsCount = Number(caf?.proposalsCount || proposals.length || 0)

  const isAmendmentFlow = caf?.isAmendment === true
  const minProposals = isAmendmentFlow ? 0 : 3
  const compliant = isAmendmentFlow ? true : (proposals.length || 0) >= minProposals

  async function addProposal() {
    if (!ensureApprovalSensitiveEditAllowed()) return
    const proposalPrice = parseCurrencyAmount(pvPrice)
    if (!pvVendor || !pvPrice || !pvFile) {
      setErr('Vendor, Price, and a PDF are required.')
      return
    }
    if (proposalPrice == null || proposalPrice < 0) {
      setErr('Please enter a valid proposal price.')
      return
    }
    if (!isPdfFile(pvFile)) {
      setErr('Please choose a .pdf file for the proposal.')
      return
    }

    try {
      setErr('')
      setUploadingProposal(true)

      const uploaded = await uploadToStorageForCaf(caf, 'proposals', pvFile)

      const row = {
        vendorName: pvVendor.trim(),
        price: proposalPrice,
        notes: pvNotes.trim(),
        recommended: false,
        file: uploaded,
        docType: 'Proposal',
        source: 'cafProposal',
      }

      const next = [...proposals, row]
      await updateCaf(caf.id, {
        proposals: next,
        proposalsCount: next.length,
        timeline: addTimeline(
          withUser(
            `Proposal added: ${row.vendorName} ($${Number(
              row.price || 0,
            ).toLocaleString()})`,
          ),
        ),
      })

      setPvVendor('')
      setPvPrice('')
      setPvNotes('')
      setPvFile(null)
      setPvFileKey((k) => k + 1)
      setCaf((prev) => ({
        ...(prev || {}),
        proposals: next,
        proposalsCount: next.length,
      }))
    } catch (e) {
      console.error('addProposal failed:', e)
      setErr(e?.message || 'Failed to add proposal.')
    } finally {
      setUploadingProposal(false)
    }
  }

  async function recommendProposal(idx) {
    if (!ensureApprovalSensitiveEditAllowed()) return
    const chosen = proposals[idx]
    if (!chosen) return

    const nextProps = proposals.map((p, i) => ({
      ...p,
      recommended: i === idx,
    }))

    try {
      await updateCaf(caf.id, {
        proposals: nextProps,
        proposalsCount: nextProps.length,
        timeline: addTimeline(
          withUser(`Proposal recommended: ${chosen.vendorName}`),
        ),
      })
      setCaf((prev) => ({
        ...(prev || {}),
        proposals: nextProps,
        proposalsCount: nextProps.length,
      }))
    } catch (e) {
      console.error(e)
      setErr('Failed to update recommendation.')
    }
  }

  async function removeProposal(idx) {
    if (!adminUser) {
      setErr('Only admins can remove CAF proposals.')
      return
    }
    if (!ensureApprovalSensitiveEditAllowed()) return
    const rem = proposals[idx]
    if (!rem) return

    const next = proposals.filter((_, i) => i !== idx)
    try {
      if (
        rem?.file?.path &&
        CAN_DELETE_BLOBS_STATUSES.has(
          String(caf.status || '').toLowerCase(),
        )
      ) {
        try {
          await deleteObject(sRef(storage, rem.file.path))
        } catch (e) {
          console.warn('blob delete (proposal):', e)
        }
      }
      await updateCaf(caf.id, {
        proposals: next,
        proposalsCount: next.length,
        timeline: addTimeline(
          withUser(`Proposal removed: ${rem?.vendorName || '—'}`),
        ),
      })
      setCaf((prev) => ({
        ...(prev || {}),
        proposals: next,
        proposalsCount: next.length,
      }))
    } catch (e) {
      console.error(e)
      setErr('Failed to remove proposal.')
    }
  }

  function requestRemoveProposal(idx) {
    setConfirmRemove({
      open: true,
      kind: 'proposal',
      idx,
    })
  }

  function startEditProposalPrice(idx) {
    const row = proposals[idx]
    if (!row) return
    setEditingProposalPriceIdx(idx)
    setEditingProposalPriceDraft(
      row.price != null && row.price !== ''
        ? String(row.price)
        : '',
    )
  }

  function cancelEditProposalPrice() {
    setEditingProposalPriceIdx(null)
    setEditingProposalPriceDraft('')
  }

  async function saveProposalPrice(idx) {
    if (!ensureApprovalSensitiveEditAllowed()) return
    const row = proposals[idx]
    if (!row) return

    const nextPrice = parseCurrencyAmount(editingProposalPriceDraft)
    if (!Number.isFinite(nextPrice) || nextPrice < 0) {
      setErr('Please enter a valid proposal price.')
      return
    }
    if (Number(row.price || 0) === nextPrice) {
      cancelEditProposalPrice()
      return
    }

    const next = proposals.map((proposal, i) => (
      i === idx ? { ...proposal, price: nextPrice } : proposal
    ))

    try {
      setErr('')
      setSavingProposalPrice(true)
      await updateCaf(caf.id, {
        proposals: next,
        proposalsCount: next.length,
        timeline: addTimeline(
          withUser(
            `Proposal price updated: ${row.vendorName} ($${Number(
              row.price || 0,
            ).toLocaleString()} → $${nextPrice.toLocaleString()})`,
          ),
        ),
      })
      setCaf((prev) => ({
        ...(prev || {}),
        proposals: next,
        proposalsCount: next.length,
      }))
      cancelEditProposalPrice()
    } catch (e) {
      console.error(e)
      setErr('Failed to update proposal price.')
    } finally {
      setSavingProposalPrice(false)
    }
  }

  function showProposalNoteTooltip(event, text) {
    const noteText = String(text || '').trim()
    if (!noteText || noteText === '-') return
    const rect = event.currentTarget.getBoundingClientRect()
    const maxWidth = 360
    const left = Math.min(rect.left, window.innerWidth - maxWidth - 16)
    setProposalNoteTooltip({
      text: noteText,
      top: rect.top - 10,
      left: Math.max(12, left),
      maxWidth,
    })
  }

  function hideProposalNoteTooltip() {
    setProposalNoteTooltip(null)
  }

  /* --------- files (documents) --------- */
  const fileList = React.useMemo(
    () => (Array.isArray(caf?.files) ? caf.files : []),
    [caf],
  )

  async function addFile() {
    const selectedDocType = fileDocType
    if (!ensureFileEditAllowed(selectedDocType)) return
    if (!fileTitle || !chosenFile) {
      setErr('File title and an upload are required.')
      return
    }
    if (!isAllowedDoc(chosenFile)) {
      setErr('Unsupported file type. Please upload a PDF or image (PNG/JPG/GIF/WebP).')
      return
    }
    if (!selectedDocType) {
      setErr('Please select a Document Type.')
      return
    }
    if (isCompletionDocTypeLabel(selectedDocType) && !isImageFile(chosenFile)) {
      setErr('Upload a JPEG, PNG, GIF, or WebP image. PDFs are not accepted as completion photos.')
      return
    }

    try {
      setErr('')
      setUploadingFile(true)
      if (isCompletionDocTypeLabel(selectedDocType)) {
        if (chosenFile.size > 15 * 1024 * 1024) throw new Error('Completion photos must be 15 MB or smaller.')
        let bitmap
        try { bitmap = await createImageBitmap(chosenFile) }
        catch { throw new Error('This file is not a readable image. PDFs cannot be used as completion photos.') }
        bitmap.close()
      }

      const uploaded = await uploadToStorageForCaf(caf, 'files', chosenFile)

      const row = {
        title: fileTitle.trim(),
        notes: fileNotes.trim(),
        tags: fileTags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        docType: selectedDocType || '',
        file: uploaded,
        ts: uploaded.ts,
        source: 'cafFile',
      }

      const next = [...(caf.files || []), row]

      setCaf((prev) => ({ ...(prev || {}), files: next }))

      await updateCaf(caf.id, {
        files: next,
        documents: next,
        attachments: next,
        docs: next,
        timeline: addTimeline(withUser(`File added: ${row.title}`)),
      })

      const fresh = normalizeCafFiles(await getCaf(caf.id))
      setCaf(fresh)

      setFileTitle('')
      setFileNotes('')
      setFileTags('')
      setFileDocType('')
      setChosenFile(null)
      setFileInputKey((k) => k + 1)
      setFilesOpen(true)
    } catch (e) {
      console.error('addFile failed:', e)
      setErr(e?.message || 'Failed to add file.')
      try {
        setCaf(normalizeCafFiles(await getCaf(caf.id)))
      } catch {}
    } finally {
      setUploadingFile(false)
    }
  }

  async function removeFile(idx) {
    if (!adminUser) {
      setErr('Only admins can remove CAF files.')
      return
    }
    const existing = Array.isArray(caf?.files) ? caf.files : []
    const removed = existing[idx]
    if (!removed) return
    if (!ensureFileEditAllowed(removed.docType || removed.type || removed.category || '')) return

    const next = existing.filter((_, i) => i !== idx)

    setCaf((prev) => ({ ...(prev || {}), files: next }))

    try {
      if (
        removed?.file?.path &&
        CAN_DELETE_BLOBS_STATUSES.has(
          String(caf.status || '').toLowerCase(),
        )
      ) {
        try {
          await deleteObject(sRef(storage, removed.file.path))
        } catch (e) {
          console.warn('blob delete (file):', e)
        }
      }
      await updateCaf(caf.id, {
        files: next,
        documents: next,
        attachments: next,
        docs: next,
        timeline: addTimeline(
          withUser(`File removed: ${removed?.title || 'Untitled'}`),
        ),
      })
      setCaf(normalizeCafFiles(await getCaf(caf.id)))
    } catch (e) {
      console.error(e)
      setErr('Failed to remove file.')
      setCaf(normalizeCafFiles(await getCaf(caf.id)))
    }
  }

  function requestRemoveFile(idx) {
    setConfirmRemove({
      open: true,
      kind: 'file',
      idx,
    })
  }

  async function confirmRemoveItem() {
    const { kind, idx } = confirmRemove
    setConfirmRemove({ open: false, kind: '', idx: null })
    if (kind === 'proposal') {
      await removeProposal(idx)
      return
    }
    if (kind === 'file') {
      await removeFile(idx)
    }
  }

  /* --------- facility type & project completion --------- */
  async function changeFacilityType(val) {
    if (!ensureApprovalSensitiveEditAllowed()) return
    if ((caf.facilityType || '') === val) return
    try {
      await updateCaf(caf.id, {
        facilityType: val || '',
        timeline: addTimeline(
          withUser(`Facility Type set to “${val || '—'}”`),
        ),
      })
      setCaf(normalizeCafFiles(await getCaf(caf.id)))
    } catch (e) {
      console.error(e)
      setErr('Failed to update facility type.')
    }
  }

  async function saveTags() {
    if (!ensureApprovalSensitiveEditAllowed()) return
    try {
      setSavingTags(true)
      const tags = parseTagsInput(tagsDraft)
      await updateCaf(caf.id, {
        tags,
        timeline: addTimeline(
          withUser(
            `Tags updated: ${tags.length ? tags.join(', ') : '—'}`,
          ),
        ),
      })
      setCaf(normalizeCafFiles(await getCaf(caf.id)))
      setTagsEditOpen(false)
      setTagsDraft(tagsToDraft(tags))
    } catch (e) {
      console.error(e)
      setErr('Failed to update tags.')
    } finally {
      setSavingTags(false)
    }
  }

  async function saveProjectCompletion() {
    if (!ensureProjectCompletionEditAllowed()) return
    try {
      // budget is read-only in the UI, but we still persist it for completeness
      const b = budget === '' ? null : parseCurrencyAmount(budget)
      const a = actual === '' ? null : parseCurrencyAmount(actual)

      const hasPhoto = completionPhotoOnFile
      let newStatus = caf.status
      let suffixNote = ''

      if (a != null) {
        if (hasPhoto) {
          newStatus = 'complete'
          suffixNote = ' (marked complete with completion photo on file)'
        } else {
          // Do NOT allow status to move to complete without a completion photo
          newStatus = caf.status
          suffixNote =
            ' (actual entered, but completion photo is still required before marking complete)'
          setErr(
              `Completion photo required before marking project complete. ` +
              `Click “Add Completion Photos”, choose an image, click “+ Add”, ` +
              `confirm it appears in the Files & Images table with Document Type “${completionDocTypeLabel}”, then click Save again.`,
          )
        }
      }

      await updateCaf(caf.id, {
        projectBudget: b,
        projectActual: a,
        status: newStatus,
        timeline: addTimeline(
          withUser(
            `Project completion saved (Budget=${b ?? '—'}, Actual=${a ?? '—'})${suffixNote}`,
          ),
        ),
      })

      const fresh = normalizeCafFiles(await getCaf(caf.id))
      setCaf(fresh)
      actualDirtyRef.current = false

      // Only fire completion tasks when we truly hit "complete"
      if (newStatus === 'complete') {
        await onCafCompletedTasksFS(fresh)
      }
    } catch (e) {
      console.error(e)
      setErr(e?.message || 'Failed to save project completion.')
    }
  }

  async function saveContractPriceChange() {
    if (!ensureApprovalSensitiveEditAllowed()) return
    const prev = caf?.contractPrice ?? null
    const notBudgeted = contractNotBudgetedDraft === true
    const next = notBudgeted ? 0 : contractDraft === '' ? null : parseCurrencyAmount(contractDraft)
    const note = (contractNote || '').trim()
    const originalApprovedAmount =
      caf?.originalApprovedAmount != null
        ? Number(caf.originalApprovedAmount)
        : null
    const amendmentDeltaAmount =
      caf?.isAmendment === true && originalApprovedAmount != null && next != null
        ? next - originalApprovedAmount
        : null
    const nextApprovalBasisAmount = getApprovalBasisAmount({
      ...caf,
      contractPrice: next,
      notBudgeted,
    })
    const nextSequence = nextApprovalBasisAmount
      ? computeSequenceForAmount(limits, nextApprovalBasisAmount).sequence
      : []
    const workflowStatusesNeedingSync = new Set([
      'awaiting_approval',
      'in_review',
      'changes_requested',
    ])
    const statusNow = String(caf?.status || '').toLowerCase()

    if (prev === next) {
      if (caf?.notBudgeted === notBudgeted) {
        setErr('Budgeted amount was not changed.')
        return
      }
    }
    if (!notBudgeted && (next == null || !Number.isFinite(next) || next <= 0)) {
      setErr('Budgeted amount must be greater than $0 unless the CAF is marked Not Budgeted.')
      return
    }
    if (!note) {
      setErr('Please provide a reason for the budgeted amount change.')
      return
    }

    try {
      setSavingContract(true)
      const changes = {
        contractPrice: next,
        notBudgeted,
        revisedTotalAmount: next,
        amendmentDeltaAmount,
        // Keep project budget tightly coupled to budgeted amount
        projectBudget: next,
        timeline: addTimeline(
          withUser(
            `${caf?.isAmendment === true ? 'Revised project total updated' : 'Budgeted amount updated'}: ${
              prev == null
                ? '—'
                : `$${Number(prev).toLocaleString()}`
            } → ${
              notBudgeted
                ? 'Not Budgeted ($0)'
                : next == null
                ? '—'
                : `$${Number(next).toLocaleString()}`
            }${
              caf?.isAmendment === true && amendmentDeltaAmount != null
                ? ` (Change: ${amendmentDeltaAmount >= 0 ? '+' : '-'}$${Math.abs(amendmentDeltaAmount).toLocaleString()})`
                : ''
            } (Reason: ${note}) — Project budget synced to budgeted amount`,
          ),
        ),
      }

      if (workflowStatusesNeedingSync.has(statusNow) && nextSequence.length) {
        const currentStage = String(caf?.stage || '').trim()
        const stageStillValid = nextSequence.some(
          (role) => normRole(role) === normRole(currentStage),
        )

        changes.approvals = {
          sequence: nextSequence,
          history: Array.isArray(caf?.approvals?.history)
            ? caf.approvals.history
            : [],
        }

        if (!stageStillValid) {
          changes.stage = nextSequence[0]
        }

        const startedStage = String(caf?.startedStage || '').trim()
        if (
          !startedStage ||
          !nextSequence.some((role) => normRole(role) === normRole(startedStage))
        ) {
          changes.startedStage = nextSequence[0]
        }
      }

      await updateCaf(caf.id, changes)
      const fresh = normalizeCafFiles(await getCaf(caf.id))
      setCaf(fresh)
      setContractEditOpen(false)
      setContractNote('')
      setErr('')

      // update local state immediately so UI reflects change before re-load
      setBudget(next != null ? String(next) : '')
    } catch (e) {
      console.error(e)
      setErr('Failed to update budgeted amount.')
    } finally {
      setSavingContract(false)
    }
  }

  async function saveProjectCompletionDate() {
    if (!ensureProjectCompletionEditAllowed()) return
    const prev = String(caf?.projectCompletionDate || '').trim()
    const next = String(completionDateDraft || '').trim()
    const note = String(completionDateNote || '').trim()
    const dateChanged = prev !== next

    if (!dateChanged) {
      setErr('Project completion date was not changed.')
      return
    }
    if (next && !isValidYmdDate(next)) {
      setErr('Please enter a valid project completion date.')
      return
    }
    if (prev && dateChanged && !note) {
      setErr('Please provide a reason for the completion date change.')
      return
    }

    const timelineText = prev
      ? `Project completion date updated: ${
          prev ? fmtYmdDate(prev) : '—'
        } → ${
          next ? fmtYmdDate(next) : '—'
        }${note ? ` (Reason: ${note})` : ''}`
      : `Project completion date set to ${next ? fmtYmdDate(next) : '—'}`

    try {
      setSavingCompletionDate(true)
      await updateCaf(caf.id, {
        projectCompletionDate: next,
        completionDateReminder: {
          sentForDate: '',
          sentAt: null,
          sentTo: [],
        },
        timeline: addTimeline(withUser(timelineText)),
      })
      const fresh = normalizeCafFiles(await getCaf(caf.id))
      setCaf(fresh)
      setCompletionDateEditOpen(false)
      setCompletionDateNote('')
      setErr('')
    } catch (e) {
      console.error(e)
      setErr('Failed to update project completion date.')
    } finally {
      setSavingCompletionDate(false)
    }
  }

  const variance = (() => {
    const isUnbudgeted = caf?.notBudgeted === true
    const hasBudget =
      budget !== '' &&
      budget != null &&
      parseCurrencyAmount(budget) != null
    const b = hasBudget ? parseCurrencyAmount(budget) : 0
    const a = parseCurrencyAmount(actual) || 0
    const diff = (a || 0) - (b || 0)
    const pct = hasBudget && b ? (diff / b) * 100 : 0
    const color = isUnbudgeted
      ? a > 0
        ? '#c62828'
        : '#6b7280'
      : hasBudget
      ? (a <= b ? '#2e7d32' : '#c62828')
      : '#6b7280'
    const text = isUnbudgeted
      ? a > 0
        ? `$${Math.abs(diff).toLocaleString()} (unbudgeted overage)`
        : 'Not budgeted'
      : hasBudget
      ? `$${Math.abs(diff).toLocaleString()} (${
          isFinite(pct) ? pct.toFixed(1) : '∞'
        }%)`
      : 'No budget set'
    return { diff, pct, color, text }
  })()

  if (loading) return <div className="card">Loading…</div>
  if (!caf)
    return (
      <div className="card messages-card">
        <h3>CAF not found</h3>
        <button onClick={() => nav('/dev/caf')}>Back to list</button>
      </div>
    )

  const prettyTitle = (f) =>
    (f.title && String(f.title).trim()) ||
    f.displayName ||
    f.name ||
    f.filename ||
    f.file?.name ||
    filenameFromUrl(f.file?.downloadURL) ||
    '—'
  const prettyTags = (f) => {
    const t = f.tags || f.tagList || f.labels || []
    if (Array.isArray(t)) return t
    if (typeof t === 'string')
      return t
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    return []
  }
  const prettyNotes = (f) =>
    (typeof f.notes === 'string' ? f.notes : '') ||
    f.note ||
    f.description ||
    ''
  const prettyFileName = (f) =>
    f.file?.name ||
    f.originalName ||
    f.filename ||
    filenameFromUrl(f.file?.downloadURL) ||
    'View Upload'
  const prettyDocType = (f) => f.docType || f.type || f.category || ''

  const { sequence, finalRole } = sequenceForAmount
  const stageMap = buildStageStatusMap(sequence, caf)
  const originalApprovedAmount =
    caf?.originalApprovedAmount != null ? Number(caf.originalApprovedAmount) : null
  const revisedTotalAmount =
    caf?.revisedTotalAmount != null ? Number(caf.revisedTotalAmount) : caf?.contractPrice ?? null
  const amendmentDeltaAmount =
    caf?.amendmentDeltaAmount != null
      ? Number(caf.amendmentDeltaAmount)
      : (caf?.isAmendment === true && originalApprovedAmount != null && revisedTotalAmount != null
          ? revisedTotalAmount - originalApprovedAmount
          : null)
  const amendmentDeltaText =
    amendmentDeltaAmount == null
      ? '—'
      : `${amendmentDeltaAmount >= 0 ? '+' : '-'}$${Math.abs(amendmentDeltaAmount).toLocaleString()}`
  const nonVoidAmendments = childAmendments.filter((row) => lc(row?.status || '') !== 'void')
  const latestAmendment =
    nonVoidAmendments.length > 0 ? nonVoidAmendments[nonVoidAmendments.length - 1] : null
  const latestApprovedAmendment =
    [...nonVoidAmendments]
      .reverse()
      .find((row) => ['approved', 'complete'].includes(lc(row?.status || ''))) || null
  const latestCloseoutAmendment = latestApprovedAmendment || latestAmendment
  const hasAmendmentChain = caf?.isAmendment !== true && nonVoidAmendments.length > 0
  const hasNewerAmendment =
    caf?.isAmendment === true &&
    nonVoidAmendments.some(
      (row) => Number(row?.amendmentNumber || 0) > Number(caf?.amendmentNumber || 0),
    )
  const latestChainAmendmentForCurrent =
    caf?.isAmendment === true ? latestAmendment : latestCloseoutAmendment
  const completionManagedElsewhere =
    hasAmendmentChain || hasNewerAmendment
  const budgetLabel =
    caf?.isAmendment === true
      ? 'Revised Project Total'
      : 'Budgeted Amount'

  const displayContract =
    hasAmendmentChain && latestCloseoutAmendment?.revisedTotalAmount != null
      ? formatMoney(Number(latestCloseoutAmendment.revisedTotalAmount))
      : caf.notBudgeted === true
      ? 'Not Budgeted ($0)'
      : revisedTotalAmount != null
      ? formatMoney(revisedTotalAmount)
      : '—'
  const displayProjectCompletionDate = fmtYmdDate(caf.projectCompletionDate)
  const activeAmendmentStatusLower = String(caf.activeAmendmentStatus || '').toLowerCase()
  const parentCompletionViaAmendment =
    caf?.isAmendment !== true && !!caf?.completedViaAmendmentId
  const activeAmendmentInProgress =
    caf?.isAmendment !== true &&
    !!caf?.activeAmendmentId &&
    activeAmendmentStatusLower !== 'complete' &&
    activeAmendmentStatusLower !== 'void'
  const rolledUpActualAmount =
    caf?.rolledUpActualAmount != null ? Number(caf.rolledUpActualAmount) : null
  const currentAuthorizedTotal =
    latestCloseoutAmendment?.revisedTotalAmount != null
      ? Number(latestCloseoutAmendment.revisedTotalAmount)
      : caf?.currentAuthorizedTotal != null
      ? Number(caf.currentAuthorizedTotal)
      : revisedTotalAmount
  const latestAmendmentNumber = latestCloseoutAmendment?.amendmentNumber || latestAmendment?.amendmentNumber || null
  const latestAmendmentTitle = latestCloseoutAmendment?.title || latestAmendment?.title || ''
  const projectCompletionOwnerText = latestAmendmentNumber
    ? `Amendment ${latestAmendmentNumber}${latestAmendmentTitle ? ` — ${latestAmendmentTitle}` : ''}`
    : ''
  const parentCardActualValue =
    hasAmendmentChain
      ? !activeAmendmentInProgress && rolledUpActualAmount != null
        ? String(rolledUpActualAmount)
        : ''
      : actual
  const amendmentRollupRows = nonVoidAmendments.map((row) => {
    const rowOriginal =
      row?.originalApprovedAmount != null ? Number(row.originalApprovedAmount) : null
    const rowRevised =
      row?.revisedTotalAmount != null ? Number(row.revisedTotalAmount) : row?.contractPrice ?? null
    const rowDelta =
      row?.amendmentDeltaAmount != null
        ? Number(row.amendmentDeltaAmount)
        : rowOriginal != null && rowRevised != null
        ? rowRevised - rowOriginal
        : null
    return {
      id: row.id,
      amendmentNumber: row.amendmentNumber,
      status: row.status,
      deltaText:
        rowDelta == null
          ? '—'
          : `${rowDelta >= 0 ? '+' : '-'}$${Math.abs(rowDelta).toLocaleString()}`,
      revisedText: rowRevised != null ? formatMoney(rowRevised) : '—',
    }
  })

  const statusLower = String(caf.status || '').toLowerCase()
  const voidLocked = statusLower === 'void'
  const finalApprovalLocked =
    statusLower === 'approved' || statusLower === 'complete' || voidLocked
  const fullyCompleteLocked = statusLower === 'complete' || voidLocked
  const postApprovalEditable = statusLower === 'approved'
  const canEditOverviewFields = !finalApprovalLocked
  const canEditCompletionDate = !fullyCompleteLocked
  const canEditMessages = !finalApprovalLocked
  const canEditProposals = !finalApprovalLocked
  const canEditFacilityType = !finalApprovalLocked
  const canManageGeneralFiles = !fullyCompleteLocked
  const canRemoveCafFiles = canManageGeneralFiles && adminUser
  const canRemoveCafProposals = canEditProposals && adminUser
  const showApprovedHint =
    statusLower === 'approved' &&
    finalRole &&
    normRole(caf.stage) === normRole(finalRole) &&
    !activeAmendmentInProgress &&
    !parentCompletionViaAmendment

  // -------- NEW BUTTON / PERMISSION LOGIC --------

// Only draft / changes_requested can (re)submit, AND user must be requester/admin/sysconfig
const requesterAllowed = hasRequesterPermission()
const showSubmit =
  requesterAllowed &&
  (statusLower === 'draft' || statusLower === 'changes_requested')

// “In approval flow” means approvers are working on it
const inApprovalFlow =
  statusLower === 'awaiting_approval' || statusLower === 'in_review'

// NEW: user-level permission (must have approver role OR be admin/sysconfig)
const userCanApprove = hasApproverPermission()

// Show Approve button only for approvers while in the approval flow
const showApprove = inApprovalFlow && userCanApprove

// We don't need to disable it if we're only showing it when inApprovalFlow,
// but keep this in case you want to reuse the flag.
const approveDisabled = false

const approveTooltip = !userCanApprove
  ? 'You do not have approval permissions for this CAF.'
  : !inApprovalFlow
  ? 'Submit CAF before approving.'
  : ''

// Only show “Reject / Send Back” when it’s in the approval flow AND user can approve
const showSendBack = inApprovalFlow && userCanApprove
const showVoid = sysconfigAdmin && statusLower !== 'void'
const showRefreshApprovalRouting = adminUser && inApprovalFlow
const showCreateAmendment =
  requesterAllowed && (statusLower === 'approved' || statusLower === 'complete')

  return (
    <div>
      {/* CAF number heading */}
<h2
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
    marginBottom: 22,
  }}
>
  {caf.number || '—'}
</h2>
      {caf.isAmendment === true && (
        <div style={{ marginTop: -12, marginBottom: 16 }}>
          <span className="badge" style={{ padding: '4px 10px' }}>
            Amendment {caf.amendmentNumber || '—'}
          </span>
          <span className="muted" style={{ marginLeft: 8 }}>
            to {caf.parentCafNumber || caf.rootCafNumber || 'original CAF'}
          </span>
        </div>
      )}

      {/* Actions bar */}
      <div
        className="mobile-stack-grid card"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr repeat(6, auto)',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <input
          placeholder="Optional comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

{showSubmit && (
  <button onClick={doSubmit}>
    Submit
  </button>
)}

{showApprove && (
  <button
    onClick={doApprove}
    disabled={approveDisabled}
    data-tooltip={approveTooltip}
  >
    Approve
  </button>
)}

{showSendBack && (
  <button className="secondary" onClick={doSendBack}>
    Reject / Send Back
  </button>
)}

{showRefreshApprovalRouting && (
  <button
    className="secondary"
    onClick={doRefreshApprovalRouting}
    disabled={refreshRoutingBusy}
  >
    {refreshRoutingBusy ? 'Refreshing…' : 'Refresh Approval Routing'}
  </button>
)}

{showCreateAmendment && (
  <button className="secondary" onClick={openAmendmentDialog}>
    Create Amendment
  </button>
)}

{showVoid && (
  <button className="danger" onClick={openVoidDialog}>
    Void CAF
  </button>
)}
      </div>

      {err && (
        <div
          className="card"
          style={{
            borderColor: '#b94a48',
            background: '#fff7f7',
            color: '#b94a48',
          }}
        >
          {err}
        </div>
      )}

      {/* Overview + Project Completion */}
      <div
        className="mobile-stack-grid card"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 0.8fr',
          gap: 20,
        }}
      >
        <div>
          <h3 style={{ marginTop: 0, marginBottom: 25 }}>Overview</h3>
          <p>
            <strong className="ov-label">Community:</strong>{' '}
            <span className="ov-value">
              {getCommunityName(caf.communityCode)}
            </span>
          </p>
          <p>
            <strong className="ov-label">Title:</strong>{' '}
            <span className="ov-value ov-wrap">
              {caf.title || '—'}
            </span>
          </p>
          <div style={{ margin: '6px 0' }}>
            <strong className="ov-label">Tags:</strong>{' '}
            {!tagsEditOpen ? (
              <span
                className="ov-value"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                {Array.isArray(caf.tags) && caf.tags.length ? (
                  <span className="pillWrap">
                    {caf.tags.map((t) => (
                      <span className="pill" key={t}>{t}</span>
                    ))}
                  </span>
                ) : (
                  <span className="muted">— none —</span>
                )}
                {canEditOverviewFields && (
                  <button
                    type="button"
                    style={{
                      background: 'transparent',
                      border: '0',
                      padding: '4px',
                      margin: 0,
                      lineHeight: 0,
                      boxShadow: 'none',
                      cursor: 'pointer',
                    }}
                    aria-label="Edit tags"
                    data-tooltip="Edit tags"
                    onClick={() => {
                      setTagsDraft(tagsToDraft(caf.tags))
                      setTagsEditOpen(true)
                    }}
                  >
                    <PencilIcon size={16} />
                  </button>
                )}
              </span>
            ) : (
              <div className="card" style={{ marginTop: 8, padding: 12 }}>
                <label>Tags (comma-separated)</label>
                <input
                  value={tagsDraft}
                  onChange={(e) => setTagsDraft(e.target.value)}
                  placeholder="e.g., roof, life-safety, urgent"
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    onClick={saveTags}
                    disabled={savingTags}
                  >
                    {savingTags ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="secondary"
                    onClick={() => {
                      setTagsEditOpen(false)
                      setTagsDraft(tagsToDraft(caf.tags))
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {caf.isAmendment === true && (
            <>
              <p>
                <strong className="ov-label">Original Approved Amount:</strong>{' '}
                <span className="ov-value">{formatMoney(originalApprovedAmount)}</span>
              </p>
              <p>
                <strong className="ov-label">Amendment Change Amount:</strong>{' '}
                <span className="ov-value">{amendmentDeltaText}</span>
              </p>
            </>
          )}

          {/* Editable Budgeted Amount */}
          <div style={{ margin: '6px 0' }}>
            <strong className="ov-label">{budgetLabel}:</strong>{' '}
            {!contractEditOpen ? (
              <span
                className="ov-value"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {displayContract}
                {canEditOverviewFields && (
                  <button
                    type="button"
                    style={{
                      background: 'transparent',
                      border: '0',
                      padding: '4px',
                      margin: 0,
                      lineHeight: 0,
                      boxShadow: 'none',
                      cursor: 'pointer',
                    }}
                    aria-label="Edit budgeted amount"
                    data-tooltip="Edit budgeted amount"
                    onClick={() => {
                      setContractDraft(
                        caf.contractPrice != null
                          ? String(caf.contractPrice)
                          : '',
                      )
                      setContractNotBudgetedDraft(caf.notBudgeted === true)
                      setContractEditOpen(true)
                    }}
                  >
                    <PencilIcon size={16} />
                  </button>
                )}
              </span>
            ) : (
              <div
                className="card"
                style={{ marginTop: 8, padding: 12 }}
              >
                <div
                  className="grid2"
                  style={{ gap: 12 }}
                >
                  <div>
                    <label>{caf?.isAmendment === true ? 'New Revised Project Total (USD)' : 'New Budgeted Amount (USD)'}</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={contractDraft}
                      onChange={(e) =>
                        setContractDraft(e.target.value)
                      }
                      placeholder="e.g., 45000"
                      disabled={contractNotBudgetedDraft}
                    />
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        cursor: 'pointer',
                        marginTop: 8,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={contractNotBudgetedDraft}
                        onChange={(e) => {
                          setContractNotBudgetedDraft(e.target.checked)
                          if (e.target.checked) setContractDraft('')
                        }}
                      />
                      Not Budgeted
                    </label>
                    <div className="muted" style={{ marginTop: 4 }}>
                      {contractNotBudgetedDraft
                        ? 'This CAF will carry a $0 budget so future spend reads as overage.'
                        : 'Enter a positive budget amount, or mark the CAF as Not Budgeted.'}
                    </div>
                  </div>
                  <div>
                    <label>
                      Reason{' '}
                      <span className="muted">(required)</span>
                    </label>
                    <input
                      value={contractNote}
                      onChange={(e) =>
                        setContractNote(e.target.value)
                      }
                      placeholder="Why is this changing?"
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    marginTop: 10,
                  }}
                >
                  <button
                    onClick={saveContractPriceChange}
                    disabled={savingContract}
                  >
                    {savingContract ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="secondary"
                    onClick={() => {
                      setContractEditOpen(false)
                      setContractNote('')
                      setContractDraft(
                        caf.contractPrice != null
                          ? String(caf.contractPrice)
                          : '',
                      )
                      setContractNotBudgetedDraft(caf.notBudgeted === true)
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={{ margin: '6px 0' }}>
            <strong className="ov-label">Project Completion Date:</strong>{' '}
            {!completionDateEditOpen ? (
              <span
                className="ov-value"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {displayProjectCompletionDate}
                {canEditCompletionDate && (
                  <button
                    type="button"
                    style={{
                      background: 'transparent',
                      border: '0',
                      padding: '4px',
                      margin: 0,
                      lineHeight: 0,
                      boxShadow: 'none',
                      cursor: 'pointer',
                    }}
                    aria-label="Edit project completion date"
                    data-tooltip="Edit project completion date"
                    onClick={() => {
                      setCompletionDateDraft(
                        String(caf.projectCompletionDate || '').trim(),
                      )
                      setCompletionDateNote('')
                      setCompletionDateEditOpen(true)
                    }}
                  >
                    <PencilIcon size={16} />
                  </button>
                )}
              </span>
            ) : (
              <div
                className="card"
                style={{ marginTop: 8, padding: 12 }}
              >
                <div
                  className="grid2"
                  style={{ gap: 12 }}
                >
                  <div>
                    <label>Project Completion Date</label>
                    <input
                      type="date"
                      value={completionDateDraft}
                      onChange={(e) =>
                        setCompletionDateDraft(e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label>
                      Explanation{' '}
                      <span className="muted">
                        {caf.projectCompletionDate
                          ? '(required if changed)'
                          : '(optional on first set)'}
                      </span>
                    </label>
                    <input
                      value={completionDateNote}
                      onChange={(e) =>
                        setCompletionDateNote(e.target.value)
                      }
                      placeholder={
                        caf.projectCompletionDate
                          ? 'Why is this date changing?'
                          : 'Optional note'
                      }
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    marginTop: 10,
                  }}
                >
                  <button
                    onClick={saveProjectCompletionDate}
                    disabled={savingCompletionDate}
                  >
                    {savingCompletionDate ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="secondary"
                    onClick={() => {
                      setCompletionDateEditOpen(false)
                      setCompletionDateDraft(
                        String(caf.projectCompletionDate || '').trim(),
                      )
                      setCompletionDateNote('')
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {(parentCafSummary || childAmendments.length > 0 || (caf.isAmendment === true && caf.amendmentReason)) && (
            <div style={{ margin: '14px 0 6px' }}>
              <strong className="ov-label">Related CAFs:</strong>
              <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                {parentCafSummary && (
                  <div className="muted">
                    Parent CAF:{' '}
                    <Link to={`/dev/caf/${encodeURIComponent(parentCafSummary.id)}`}>
                      {parentCafSummary.number || parentCafSummary.id}
                    </Link>
                    {parentCafSummary.title ? ` — ${parentCafSummary.title}` : ''}
                  </div>
                )}
                {activeAmendmentInProgress && (
                  <div className="muted">
                    Active Amendment:{' '}
                    <Link to={`/dev/caf/${encodeURIComponent(caf.activeAmendmentId)}`}>
                      {caf.activeAmendmentId}
                    </Link>
                    {caf.activeAmendmentNumber ? ` — Amendment ${caf.activeAmendmentNumber}` : ''}
                    {caf.activeAmendmentStatus ? ` (${caf.activeAmendmentStatus})` : ''}
                  </div>
                )}
                {hasNewerAmendment && latestAmendment && (
                  <div className="muted">
                    Newer Amendment:{' '}
                    <Link to={`/dev/caf/${encodeURIComponent(latestAmendment.id)}`}>
                      {latestAmendment.number || latestAmendment.id}
                    </Link>
                    {latestAmendment.amendmentNumber ? ` — Amendment ${latestAmendment.amendmentNumber}` : ''}
                    {latestAmendment.status ? ` (${latestAmendment.status})` : ''}
                  </div>
                )}
                {parentCompletionViaAmendment && (
                  <div className="muted">
                    Project Completion:{' '}
                    <Link to={`/dev/caf/${encodeURIComponent(caf.completedViaAmendmentId)}`}>
                      {caf.completedViaAmendmentId}
                    </Link>
                    {caf.completedViaAmendmentNumber ? ` — Amendment ${caf.completedViaAmendmentNumber}` : ''}
                    {caf.completedViaAmendmentAt ? ` — closed out ${fmtTS(caf.completedViaAmendmentAt)}` : ''}
                    {currentAuthorizedTotal != null ? ` — Revised total ${formatMoney(currentAuthorizedTotal)}` : ''}
                    {rolledUpActualAmount != null ? ` — Actual ${formatMoney(rolledUpActualAmount)}` : ''}
                  </div>
                )}
                {childAmendments.length > 0 && (
                  <div>
                    <div className="muted" style={{ marginBottom: 4 }}>
                      Amendments
                    </div>
                    <div style={{ display: 'grid', gap: 4 }}>
                      {childAmendments.map((child) => (
                        <div key={child.id} className="muted">
                          <Link to={`/dev/caf/${encodeURIComponent(child.id)}`}>
                            {child.number || child.id}
                          </Link>{' '}
                          — Amendment {child.amendmentNumber || '—'}
                          {child.title ? ` — ${child.title}` : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {caf.isAmendment === true && caf.amendmentReason && (
                  <div className="muted">
                    Amendment Reason: <strong>{caf.amendmentReason}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          <p>
            <strong className="ov-label">Status:</strong>{' '}
            <span className="ov-value">
              <span className="badge">
                {caf.status || '—'}
              </span>
            </span>
          </p>
          {showApprovedHint && (
            <p
              style={{
                marginTop: 4,
                color: '#1e7e34',
                fontWeight: 500,
              }}
            >
              Approved — awaiting project completion entry
            </p>
          )}
          {activeAmendmentInProgress && (
            <p
              style={{
                marginTop: 4,
                color: '#1e7e34',
                fontWeight: 500,
              }}
            >
              Project closeout is being managed through Amendment {caf.activeAmendmentNumber || '—'}.
            </p>
          )}
          {hasNewerAmendment && (
            <p
              style={{
                marginTop: 4,
                color: '#1e7e34',
                fontWeight: 500,
              }}
            >
              This amendment is historical. Project closeout now belongs to {projectCompletionOwnerText || 'the latest amendment'}.
            </p>
          )}
          {parentCompletionViaAmendment && (
            <p
              style={{
                marginTop: 4,
                color: '#1e7e34',
                fontWeight: 500,
              }}
            >
              Project closeout was completed through Amendment {caf.completedViaAmendmentNumber || '—'}.
            </p>
          )}
          {finalApprovalLocked && (
            <p
              className="muted"
              style={{ marginTop: 4 }}
            >
              {voidLocked
                ? 'This CAF has been voided and is excluded from workflow reporting.'
                : fullyCompleteLocked
                ? 'This CAF is complete and locked from further changes.'
                : hasNewerAmendment
                ? `This amendment remains part of the project history. Project completion and final actuals must be entered on ${projectCompletionOwnerText || 'the latest amendment record'}.`
                : activeAmendmentInProgress
                ? 'This CAF remains the original approved record. Project completion and revised scope are being managed through the active amendment.'
                : parentCompletionViaAmendment
                ? 'This CAF remains the original approved record. Project completion was captured through its amendment record.'
                : 'This CAF is locked after final approval. Only the project completion date, actual amount, and files/documents can be updated until it is marked complete.'}
            </p>
          )}
          {voidLocked && (
            <p
              className="muted"
              style={{ marginTop: 4 }}
            >
              Reason: <strong>{caf.voidReason || '—'}</strong>
              {caf.voidedBy ? ` — ${caf.voidedBy}` : ''}
            </p>
          )}
          <p>
            <strong className="ov-label">Stage:</strong>{' '}
            <span className="ov-value">
              {caf.stage || '—'}
            </span>
          </p>

          <div style={{ marginTop: 8 }}>
            <strong
              className="ov-label"
              style={{
                display: 'block',
                marginBottom: 6,
              }}
            >
              Approver Chain:
            </strong>
            {sequence?.length ? (
              <span className="ov-value">
                <span className="pillWrap">
                  {sequence.map((r) => {
                    const state = stageMap.get(r) || 'pending'
                    const style = styleForStageState(state)
                    return (
                      <span
                        className="pill"
                        key={r}
                        style={style}
                        data-tooltip={state} tabIndex={0}
                      >
                        {r}
                      </span>
                    )
                  })}
                </span>
              </span>
            ) : (
              <span className="muted">
                No limits configured
              </span>
            )}
            {finalRole && (
              <div
                className="muted"
                style={{ marginTop: 6 }}
              >
                Final approver for this amount:{' '}
                <strong>{finalRole}</strong>
              </div>
            )}

            {sysconfigAdmin && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 8,
                  fontSize: 12,
                }}
              >
                <input
                  type="checkbox"
                  checked={overrideApprovalChain}
                  onChange={(e) =>
                    setOverrideApprovalChain(e.target.checked)
                  }
                />
                <span>Allow override for next approval action</span>
              </label>
            )}
          </div>
        </div>

        <div>
          {/* Facility Type */}
          <div style={{ marginTop: 47 }}>
            <strong className="ov-label">Facility Type:</strong>{' '}
            {!facilityEditOpen ? (
              <span
                className="ov-value"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {caf.facilityType ? (
                  <span className="pill">
                    {caf.facilityType}
                  </span>
                ) : (
                  <span className="muted">
                    — none selected —
                  </span>
                )}
                {canEditFacilityType && (
                  <button
                    type="button"
                    style={{
                      background: 'transparent',
                      border: '0',
                      padding: '4px',
                      margin: 0,
                      lineHeight: '0',
                      boxShadow: 'none',
                      cursor: 'pointer',
                    }}
                    aria-label="Edit facility type"
                    data-tooltip="Edit facility type"
                    onClick={() =>
                      setFacilityEditOpen(true)
                    }
                  >
                    <PencilIcon size={16} />
                  </button>
                )}
              </span>
            ) : (
              <div style={{ marginTop: 8 }}>
                <select
                  value={caf.facilityType || ''}
                  onChange={async (e) => {
                    await changeFacilityType(e.target.value)
                    setFacilityEditOpen(false)
                  }}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    width: '100%',
                    maxWidth: 280,
                  }}
                >
                  <option value="">
                    — Select Facility Category —
                  </option>
                  {facilityTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: 8 }}>
                  <button
                    className="secondary"
                    onClick={() =>
                      setFacilityEditOpen(false)
                    }
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          <div>
            <div style={{ marginBottom: 12 }}>
              <label>Created</label>
              <div>{fmtTS(caf.createdAt)}</div>
            </div>
            <div>
              <label>Updated</label>
              <div>{fmtTS(caf.updatedAt)}</div>
            </div>
          </div>
        </div>

        <div
          className="card"
          style={{
            margin: 0,
            marginTop: 8,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <h3 style={{ marginTop: 0 }}>Project Completion</h3>
          <div className="grid2" style={{ gap: 12 }}>
            <div>
              <label>{budgetLabel}</label>
              <input
                value={hasAmendmentChain ? (currentAuthorizedTotal != null ? String(currentAuthorizedTotal) : '') : budget}
                readOnly
                inputMode="decimal"
                placeholder="0"
                style={{
                  background: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                  color: 'var(--input-text)',
                  opacity: 0.85,
                  cursor: 'not-allowed',
                }}
              />
            </div>
            <div>
              <label>Actual Amount</label>
              <input
                value={parentCardActualValue}
                onChange={(e) =>
                  {
                    actualDirtyRef.current = true
                    setActual(e.target.value)
                  }
                }
                inputMode="decimal"
                placeholder="0"
                disabled={fullyCompleteLocked || completionManagedElsewhere}
              />
            </div>
          </div>
          <div
            style={{
              marginTop: 8,
              color: variance.color,
              fontWeight: 700,
            }}
          >
            {variance.text}
          </div>
          {hasAmendmentChain && (
            <div className="muted" style={{ marginTop: 4, fontSize: 12, display: 'grid', gap: 3 }}>
              <div>Original Approved Amount: {formatMoney(caf.contractPrice)}</div>
              {amendmentRollupRows.map((row) => (
                <div key={row.id}>
                  Amendment {row.amendmentNumber || '—'} Change: {row.deltaText} · Total: {row.revisedText}
                </div>
              ))}
              <div>Current Authorized Total: {currentAuthorizedTotal != null ? formatMoney(currentAuthorizedTotal) : '—'}</div>
              {projectCompletionOwnerText ? (
                <div>Project completion owner: {projectCompletionOwnerText}</div>
              ) : null}
            </div>
          )}
          {caf.isAmendment === true && (
            <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
              Original Approved Amount: {formatMoney(originalApprovedAmount)} · Change: {amendmentDeltaText}
            </div>
          )}
          <div
            style={{ marginTop: 4, fontSize: 12 }}
          >
            {completionPhotoOnFile ? (
              <span
                style={{
                  color: '#2e7d32',
                  fontWeight: 600,
                }}
              >
                {completionManagedElsewhere
                  ? `Completion photo and project closeout are managed through ${projectCompletionOwnerText || 'the latest amendment record'}.`
                  : `Completion photo on file (Document Type: “${completionDocTypeLabel}”).`}
              </span>
            ) : (
              <span
                style={{
                  color: '#c62828',
                  fontWeight: 600,
                }}
              >
                {completionManagedElsewhere
                  ? `Completion photo and final closeout must be completed on ${projectCompletionOwnerText || 'the latest amendment record'}.`
                  : `Completion photo required before status can be marked complete. Upload an image in Files & Images with Document Type “${completionDocTypeLabel}”.`}
              </span>
            )}
          </div>

          {!completionManagedElsewhere && (
            <div style={{ marginTop: 'auto' }}>
              <button
                className="btn secondary"
                onClick={goToCompletionPhotos}
                style={{
                  width: '100%',
                  marginBottom: 8,
                }}
                disabled={fullyCompleteLocked}
              >
                Add Completion Photos
              </button>
              <button
                className="btn primary"
                onClick={saveProjectCompletion}
                style={{ width: '100%' }}
                disabled={fullyCompleteLocked}
              >
                Save
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <h3 style={{ margin: 0 }}>Messages</h3>
          <span className="muted">
            {(caf.messages || []).length}
          </span>
          <div style={{ flex: 1 }} />
          <button
            className="secondary"
            onClick={() =>
              setMsgsOpen((v) => !v)
            }
          >
            {msgsOpen ? 'Hide' : 'Show'}
          </button>
        </div>

        {msgsOpen && (
          <>
            {canEditMessages ? (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  marginTop: 8,
                }}
              >
                <div style={{ flex: 1, position: 'relative' }}>
                  <textarea
                    ref={msgTextareaRef}
                    rows={3}
                    style={{
                      flex: 1,
                      resize: 'vertical',
                    }}
                    placeholder="Write a quick update or question… Type @ to mention someone by name. (Enter to post)"
                    value={msgText}
                    onChange={(e) => {
                      setMsgText(e.target.value)
                      syncMentionMenu(e.target.value, e.target.selectionStart || 0)
                    }}
                    onClick={(e) => {
                      syncMentionMenu(msgText, e.currentTarget.selectionStart || 0)
                    }}
                    onKeyDown={(e) => {
                      if (mentionMenu.open && mentionSuggestions.length) {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault()
                          setMentionMenu((prev) => ({
                            ...prev,
                            activeIndex: (prev.activeIndex + 1) % mentionSuggestions.length,
                          }))
                          return
                        }
                        if (e.key === 'ArrowUp') {
                          e.preventDefault()
                          setMentionMenu((prev) => ({
                            ...prev,
                            activeIndex:
                              (prev.activeIndex - 1 + mentionSuggestions.length) %
                              mentionSuggestions.length,
                          }))
                          return
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault()
                          resetMentionMenu()
                          return
                        }
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          applyMention(mentionSuggestions[mentionMenu.activeIndex] || mentionSuggestions[0])
                          return
                        }
                      }
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        postMessage()
                      }
                    }}
                  />
                  {mentionMenu.open && mentionSuggestions.length > 0 && (
                    <div
                      className="message-mention-menu"
                      style={{
                        top: mentionMenuPos.top,
                        left: mentionMenuPos.left,
                        width: mentionMenuPos.width ? `${mentionMenuPos.width}px` : '300px',
                      }}
                    >
                      {mentionSuggestions.map((user, idx) => (
                        <button
                          key={user.email}
                          type="button"
                          className={`message-mention-item${idx === mentionMenu.activeIndex ? ' is-active' : ''}`}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            applyMention(user)
                          }}
                        >
                          <span className="message-mention-name">{user.name}</span>
                          <span className="message-mention-email">{user.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={postMessage}>
                  Post Message
                </button>
              </div>
            ) : (
              <p
                className="muted"
                style={{ marginTop: 8 }}
              >
                {voidLocked
                  ? 'This CAF has been voided and messages are locked.'
                  : 'Messages are locked after final approval.'}
              </p>
            )}
            {(caf.messages || []).length === 0 ? (
              <p
                className="muted"
                style={{ marginTop: 8 }}
              >
                No messages yet.
              </p>
            ) : (
              <ul style={{ marginTop: 12 }}>
                {caf.messages
                  .slice()
                  .reverse()
                  .map((m, i) => (
                    <li key={i}>
                      <strong>{m.by}</strong> —{' '}
                      <em>{fmtTS(m.ts)}</em>
                      <div>{m.body}</div>
                    </li>
                  ))}
              </ul>
            )}
          </>
        )}
      </div>

      {/* Proposals */}
      <div className="card">
        <h3>Proposals</h3>
        <p style={{ marginBottom: 8 }}>
          Submitted:{' '}
          <strong>{proposalsCount}</strong>{' '}
          {isAmendmentFlow ? (
            <span className="muted">
              — amendment workflow: 3 proposals are not required, but the revised scope and updated proposal from the selected provider must be attached
            </span>
          ) : (
            <>
              (min required: {minProposals}){' '}
              {compliant ? (
                <span
                  className="badge"
                  style={{ background: '#28a745' }}
                >
                  Compliant
                </span>
              ) : (
                <span
                  className="badge"
                  style={{ background: '#e0a800' }}
                >
                  Needs{' '}
                  {Math.max(
                    0,
                    minProposals -
                      (proposals.length || 0),
                  )}{' '}
                  more
                </span>
              )}
            </>
          )}
        </p>
        {isAmendmentFlow && proposalsCount > 0 && (
          <p className="muted" style={{ marginTop: -2, marginBottom: 12 }}>
            Recommended proposal(s) from the original CAF were carried into this amendment for reference.
          </p>
        )}

        <div className="proposals-head">
          {canEditProposals ? (
          <div className="proposals-form">
            <div className="mobile-stack-grid"
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns:
                  '1.6fr 1fr 2fr 1.6fr 205px',
                alignItems: 'end',
              }}
            >
              <div>
                <label>Vendor</label>
                <input
                  value={pvVendor}
                  onChange={(e) =>
                    setPvVendor(e.target.value)
                  }
                  placeholder="e.g., ABC Contractors"
                />
              </div>
              <div>
                <label>Price</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={pvPrice}
                  onChange={(e) =>
                    setPvPrice(e.target.value)
                  }
                  placeholder="e.g., 25000"
                />
              </div>
              <div>
                <label>Notes</label>
                <input
                  value={pvNotes}
                  onChange={(e) =>
                    setPvNotes(e.target.value)
                  }
                  placeholder="optional"
                />
              </div>
              <div>
                <label>Proposal (PDF)</label>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <input
                    id={`pv-file-input-${pvFileKey}`}
                    key={pvFileKey}
                    type="file"
                    accept="application/pdf"
                    onChange={(e) =>
                      setPvFile(
                        e.target.files &&
                          e.target.files.length
                          ? e.target.files[0]
                          : null,
                      )
                    }
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor={`pv-file-input-${pvFileKey}`}
                    className="btn"
                    style={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      height: 40,
                      display: 'inline-flex',
                      alignItems: 'center',
                      paddingInline: 12,
                      fontWeight: 600,
                      position: 'relative',
                      top: '6px',
                    }}
                  >
                    Choose File
                  </label>
                  <span
                    style={{
                      height: 40,
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: 'var(--color-text)',
                      fontWeight: 600,
                      fontSize: 12,
                      opacity: 0.75,
                      position: 'relative',
                      top: '6px',
                      userSelect: 'none',
                    }}
                  >
                    {pvFile ? pvFile.name : 'No file chosen'}
                  </span>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  className="btn primary"
                  onClick={addProposal}
                  disabled={uploadingProposal}
                >
                  {uploadingProposal ? 'Adding…' : '+ Add'}
                </button>
              </div>
            </div>
          </div>
          ) : (
            <p className="muted" style={{ marginTop: 8 }}>
              {voidLocked
                ? 'This CAF has been voided and proposals are locked.'
                : 'Proposals are locked after final approval.'}
            </p>
          )}
        </div>

        <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table
          className="table proposals-table"
          style={{ marginTop: 12 }}
        >
          <colgroup>
            <col style={{ width: '17%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '19%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Price</th>
              <th>Notes</th>
              <th>Document</th>
              <th>Recommended</th>
              <th className="actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(proposals || []).map((p, idx) => (
              <tr key={idx}>
                <td>{p.vendorName}</td>
                <td>
                  {canEditProposals && editingProposalPriceIdx === idx ? (
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <input
                        type="text"
                        inputMode="decimal"
                        value={editingProposalPriceDraft}
                        onChange={(e) =>
                          setEditingProposalPriceDraft(e.target.value)
                        }
                        style={{ width: 120 }}
                        placeholder="Price"
                      />
                      <button
                        type="button"
                        onClick={() => saveProposalPrice(idx)}
                        disabled={savingProposalPrice}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        onClick={cancelEditProposalPrice}
                        disabled={savingProposalPrice}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      $
                      {Number(
                        p.price || 0,
                      ).toLocaleString()}
                      {canEditProposals && (
                        <button
                          type="button"
                          style={{
                            background: 'transparent',
                            border: '0',
                            padding: '4px',
                            margin: 0,
                            lineHeight: 0,
                            boxShadow: 'none',
                            cursor: 'pointer',
                          }}
                          aria-label="Edit proposal price"
                          data-tooltip="Edit proposal price"
                          onClick={() => startEditProposalPrice(idx)}
                        >
                          <PencilIcon size={14} />
                        </button>
                      )}
                    </span>
                  )}
                </td>
                <td>
                  {p.notes ? (
                    <button
                      type="button"
                      className="proposal-note-trigger"
                      onMouseEnter={(e) => showProposalNoteTooltip(e, p.notes)}
                      onMouseLeave={hideProposalNoteTooltip}
                      onFocus={(e) => showProposalNoteTooltip(e, p.notes)}
                      onBlur={hideProposalNoteTooltip}
                    >
                      <span className="proposal-note-text">{p.notes}</span>
                    </button>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  {p.file?.downloadURL ? (
                    <a
                      href={p.file.downloadURL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {p.file?.name ||
                        filenameFromUrl(
                          p.file?.downloadURL,
                        ) ||
                        'View PDF'}
                    </a>
                  ) : (
                    <em>—</em>
                  )}
                </td>
                <td>
                  {p.recommended ? (
                    <span className="badge">
                      Yes
                    </span>
                  ) : (
                    'No'
                  )}
                </td>
                <td
                  className="actions"
                  style={{ paddingRight: '16px' }}
                >
                  {canEditProposals ? (
                    <div
                      className="btn-row"
                      style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 8,
                        width: 260,
                        marginRight: 'auto',
                        paddingRight: '130px',
                      }}
                    >
                      <button
                        onClick={() =>
                          recommendProposal(idx)
                        }
                      >
                        {p.recommended
                          ? 'Keep Recommended'
                          : 'Mark Recommended'}
                      </button>
                      {canRemoveCafProposals && (
                        <button
                          className="secondary"
                          onClick={() =>
                            requestRemoveProposal(idx)
                          }
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="muted">Locked</span>
                  )}
                </td>
              </tr>
            ))}
            {(proposals || []).length === 0 && (
              <tr>
                <td colSpan={6}>
                  <div className="muted">
                    No proposals yet.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table></div>
      </div>

      {/* Files (Documents) */}
      <div id="caf-files-card" className="card">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <h3 style={{ margin: 0 }}>
            Files & Images
            <span className="muted">
              ({fileList.length})
            </span>
          </h3>
          <div style={{ flex: 1 }} />
          <button
            className="secondary"
            onClick={() =>
              setFilesOpen((v) => !v)
            }
          >
            {filesOpen ? 'Hide' : 'Show'}
          </button>
        </div>

        {filesOpen && (
          <>
            {canManageGeneralFiles ? (
            <div className="mobile-stack-grid"
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns:
                  '1.6fr 2fr 1.1fr 1.4fr 1.6fr auto',
                alignItems: 'end',
                marginTop: 12,
                marginBottom: 12,
              }}
            >
              <div>
                <label>Title</label>
                <input
                  value={fileTitle}
                  onChange={(e) =>
                    setFileTitle(e.target.value)
                  }
                  placeholder="e.g., Signed proposal (PDF)"
                />
              </div>
              <div>
                <label>Notes</label>
                <input
                  value={fileNotes}
                  onChange={(e) =>
                    setFileNotes(e.target.value)
                  }
                  placeholder="optional"
                />
              </div>
              <div>
                <label>Document Type</label>
                <select
                  value={fileDocType}
                  onChange={(e) =>
                    setFileDocType(e.target.value)
                  }
                >
                  <option value="">
                    — Select Type —
                  </option>
                  {docTypes.map((dt) => (
                    <option
                      key={dt.id}
                      value={dt.name}
                    >
                      {dt.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>
                  Tags{' '}
                  <span className="muted">
                    (comma-separated)
                  </span>
                </label>
                <input
                  value={fileTags}
                  onChange={(e) =>
                    setFileTags(e.target.value)
                  }
                  placeholder="proposal, signed, phase1"
                />
              </div>
              <div>
                <label>Choose File</label>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <input
                    ref={fileInputRef}
                    id={`custom-file-input-${fileInputKey}`}
                    key={fileInputKey}
                    type="file"
                    accept={isCompletionDocTypeLabel(fileDocType) ? 'image/png,image/jpeg,image/gif,image/webp' : 'application/pdf,image/png,image/jpeg,image/gif,image/webp'}
                    onChange={(e) =>
                      setChosenFile(
                        e.target.files &&
                          e.target.files.length
                          ? e.target.files[0]
                          : null,
                      )
                    }
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor={`custom-file-input-${fileInputKey}`}
                    className="btn"
                    style={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      height: 40,
                      display: 'inline-flex',
                      alignItems: 'center',
                      paddingInline: 12,
                      fontWeight: 600,
                      position: 'relative',
                      top: '6px',
                    }}
                  >
                    Choose File
                  </label>
                  <span
                    style={{
                      height: 40,
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: 'var(--color-text)',
                      fontWeight: 600,
                      fontSize: 12,
                      opacity: 0.75,
                      position: 'relative',
                      top: '6px',
                      userSelect: 'none',
                    }}
                  >
                    {chosenFile
                      ? chosenFile.name
                      : 'No file chosen'}
                  </span>
                </div>
              </div>
              <div>
                <button
                  className="btn primary"
                  onClick={addFile}
                  disabled={uploadingFile}
                >
                  {uploadingFile ? 'Uploading…' : '+ Add'}
                </button>
              </div>
            </div>
            ) : (
              <p className="muted" style={{ marginTop: 12, marginBottom: 12 }}>
                {voidLocked
                  ? 'This CAF has been voided and files are locked.'
                  : 'Files are locked after this CAF is marked complete.'}
              </p>
            )}

            {postApprovalEditable && (
              <p className="muted" style={{ marginTop: 0, marginBottom: 12 }}>
                {`After final approval, you can still upload supporting documents. Use Document Type “${completionDocTypeLabel}” for the final image needed to mark the CAF complete.`}
              </p>
            )}

            {fileList.length === 0 ? (
              <p
                className="muted"
                style={{ marginTop: 8 }}
              >
                No documents uploaded yet.
              </p>
            ) : (
              <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table
                className="table"
                style={{ marginTop: 4 }}
              >
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Tags</th>
                    <th>Filename</th>
                    <th>Notes</th>
                    <th>Uploaded</th>
                    <th className="actions">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fileList.map((f, i) => (
                    <tr
                      key={`${prettyTitle(f)}-${i}`}
                    >
                      <td>{prettyTitle(f)}</td>
                      <td>
                        {prettyDocType(f) || (
                          <em>—</em>
                        )}
                      </td>
                      <td>
                        {prettyTags(f).length
                          ? prettyTags(f).join(', ')
                          : (
                          <em>—</em>
                            )}
                      </td>
                      <td>
                        {f.file?.downloadURL ? (
                          <a
                            href={
                              f.file.downloadURL
                            }
                            target="_blank"
                            rel="noreferrer"
                          >
                            {prettyFileName(f)}
                          </a>
                        ) : (
                          <em>—</em>
                        )}
                      </td>
                      <td>
                        {prettyNotes(f) || (
                          <em>—</em>
                        )}
                      </td>
                      <td>{fmtTS(f.ts)}</td>
                      <td className="actions">
                        {canRemoveCafFiles ? (
                          <div
                            className="btn-row"
                            style={{
                              display: 'flex',
                              flexWrap: 'nowrap',
                              gap: 8,
                              justifyContent:
                                'flex-end',
                            }}
                          >
                            <button
                              className="secondary"
                              onClick={() =>
                                requestRemoveFile(i)
                              }
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <span className="muted">Locked</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </>
        )}
      </div>

      {proposalNoteTooltip && (
        <div
          className="proposal-note-tooltip"
          style={{
            top: proposalNoteTooltip.top,
            left: proposalNoteTooltip.left,
            maxWidth: proposalNoteTooltip.maxWidth,
          }}
          role="tooltip"
        >
          {proposalNoteTooltip.text}
        </div>
      )}

      <ConfirmDialog
        open={confirmRemove.open}
        title={
          confirmRemove.kind === 'proposal'
            ? 'Remove Proposal'
            : 'Remove Document'
        }
        message={
          confirmRemove.kind === 'proposal'
            ? 'Remove this proposal? This cannot be undone.'
            : 'Remove this document? This cannot be undone.'
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={confirmRemoveItem}
        onCancel={() =>
          setConfirmRemove({ open: false, kind: '', idx: null })
        }
      />

      <ConfirmDialog
        open={submitValidationDialog.open}
        title={submitValidationDialog.title || 'Missing Submission Details'}
        message={submitValidationDialog.message}
        confirmLabel="OK"
        hideCancel
        tone="default"
        onConfirm={() =>
          setSubmitValidationDialog({ open: false, title: '', message: '' })
        }
        onCancel={() =>
          setSubmitValidationDialog({ open: false, title: '', message: '' })
        }
      />

      <ConfirmDialog
        open={voidDialog.open}
        title="Void CAF"
        message={(
          <div>
            <p style={{ marginTop: 0 }}>
              Voided CAFs are removed from workflow reporting and all linked tasks are closed. This preserves history.
            </p>
            <label htmlFor="void-caf-reason">Reason</label>
            <textarea
              id="void-caf-reason"
              rows={3}
              style={{ width: '100%', resize: 'vertical', marginTop: 6 }}
              value={voidDialog.reason}
              onChange={(e) =>
                setVoidDialog((prev) => ({
                  ...prev,
                  reason: e.target.value,
                  error: '',
                }))
              }
              placeholder="Why is this CAF being voided?"
            />
            {voidDialog.error && (
              <p style={{ color: '#b94a48', marginBottom: 0 }}>
                {voidDialog.error}
              </p>
            )}
          </div>
        )}
        confirmLabel={voidDialog.saving ? 'Voiding…' : 'Void CAF'}
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={doVoidCaf}
        onCancel={() =>
          setVoidDialog({ open: false, reason: '', error: '', saving: false })
        }
      />
      <ConfirmDialog
        open={amendmentDialog.open}
        title="Create Amendment"
        message={
          <div style={{ display: 'grid', gap: 10 }}>
            <p className="muted" style={{ margin: 0 }}>
              This will create a new draft amendment linked to this CAF. The amendment must be submitted and fully approved before revised scope, budget, or timeline changes are authorized.
            </p>
            <div>
              <label htmlFor="amendment-reason">Reason</label>
              <textarea
                id="amendment-reason"
                rows={4}
                value={amendmentDialog.reason}
                onChange={(e) =>
                  setAmendmentDialog((prev) => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
                placeholder="Why does this approved CAF need an amendment?"
              />
            </div>
            {amendmentDialog.error && (
              <div style={{ color: '#b94a48' }}>{amendmentDialog.error}</div>
            )}
          </div>
        }
        confirmLabel={amendmentDialog.saving ? 'Creating…' : 'Create Amendment'}
        cancelLabel="Cancel"
        onConfirm={doCreateAmendment}
        onCancel={() =>
          setAmendmentDialog({ open: false, reason: '', error: '', saving: false })
        }
      />

      {/* Timeline */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>
            Timeline{' '}
            <span className="muted">
              {(caf.timeline || []).length}
            </span>
          </h3>
          <button
            className="btn"
            onClick={() =>
              setTimelineOpen((o) => !o)
            }
            aria-expanded={timelineOpen}
            aria-controls="caf-timeline-panel"
          >
            {timelineOpen ? 'Hide' : 'Show'}
          </button>
        </div>

        {timelineOpen &&
          ((caf.timeline || []).length === 0 ? (
            <p
              className="muted"
              style={{ marginTop: 8 }}
            >
              No activity yet.
            </p>
          ) : (
            <ul
              id="caf-timeline-panel"
              style={{ marginTop: 12 }}
            >
              {(caf.timeline || []).map(
                (t, i) => (
                  <li key={i}>
                    <strong>
                      {new Date(
                        t.ts,
                      ).toLocaleString()}
                    </strong>{' '}
                    — {t.event}
                  </li>
                ),
              )}
            </ul>
          ))}
      </div>

      <p>
        <button
          className="secondary"
          onClick={() => nav('/dev/caf')}
        >
          ← Back to list
        </button>
      </p>
    </div>
  )
}
